// Porto, "Gold over the Douro": a drizzly autumn night on the Cais de Gaia, among the port lodges, looking north-east
// across the Douro. From the Gaia quay (41.137 N, 8.614 W) the view runs, left to right: Torre dos Clérigos on the
// skyline (bearing about 0°), the Ribeira stacked up its hill (15°), the Sé and the bishop's palace on the hilltop (24°),
// the Dom Luís I bridge (55°) and Gaia's own bank with the Serra do Pilar monastery on its bluff (81°). That 80° is
// compressed into the frame without changing the order; the bridge, seen about 40° off its side from the quay, is drawn
// side-on, and the Clérigos tower is drawn nearer (larger) than it stands so it rises well over the roofs.
// Back to front: deep blue sky, stars, a big moon, a few wisps -> the upriver valley seen through the arch -> the
// Clérigos tower, the Sé, the Ribeira's tall narrow houses in tiers down to the arcades of the Cais da Ribeira, the
// Gaia bluff and Serra do Pilar -> the Dom Luís I bridge (granite abutments, the iron arch floodlit gold, the upper deck
// resting on its crown and running out over iron trestles to both hills, the lower deck hung from it above the river)
// -> the Douro (dark green-blue, mirrored gold) with rabelo boats moored -> the Gaia quay's granite kerb and setts, a
// lamp, bollards, a gull -> a port lodge on the right (the cozy corner): its arched door open on the barrels, an
// azulejo panel, a terrace of barrel tables with people at them, and the lodge's roof sign (the billboard).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, px, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 160;                     // far waterline: the Cais da Ribeira and the bridge's feet
const KERB = 198;                   // top of the Gaia quay's granite kerb: the Douro shows from WL down to it
const FEET = 205;                   // the quay's people stand on this row (above the bottom 63 rows)
const MOON = { x: 346, y: 33, r: 6 };
// Dom Luís I (1886): the arch springs from granite abutments at YB; its inner curve (intrados) spans XC ± HSI and
// rises RISEI, its outer curve (extrados) spans XC ± HSE and rises RISEE, so the iron band is deepest at the feet and
// thinnest at the crown, where it carries the upper deck (rows TOP..BOT). The lower deck (LTOP..LBOT) is hung from it.
const BR = { xc: 297, yb: 152, hsI: 103, riseI: 54, hsE: 114, riseE: 59, top: 86, bot: 92, ltop: 143, lbot: 148,
  d0: 186, d1: 446, l0: 176, l1: 418 };
const TRESTLES = [{ x: BR.xc - 106, foot: 151, w: 11 }, { x: BR.xc + 106, foot: 151, w: 11 }, { x: 425, foot: 118, w: 7 }];
const POSTS = [BR.xc - 68, BR.xc - 34, BR.xc + 33, BR.xc + 67];   // iron columns standing on the arch under the upper deck
const CL = { x: 131, tip: 28, base: 103 };             // Torre dos Clérigos: centre column, cross tip, foot (hidden)
const SE = { x0: 150, x1: 181, crest: 86 };            // the Sé's two towers on the hilltop
const LODGE = { x0: 414, eave: 128, floor: 206 };      // the port lodge (cozy corner) on the right
const BB = { x: 420, y: 96, w: 50, h: 20 };            // the lodge's roof sign: the billboard's face
const LAMP = { x: 26, y: 134 };                        // the quay lamp's lantern (left, near the viewer)
const BENCH = { x0: 214, x1: 252, y: 238 };            // granite bench near the viewer, facing the river
const DECK = { x0: 354 };                              // the lodge terrace's timber deck runs from here to the right edge
const TERRACE = 204;                                   // feet row on the terrace deck

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line through a put(x, y) function
function bres(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
// pick from a dark-to-light ramp with ordered dither
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}

/* ================= Sign font: FONT3 with wide M, W, N, K, V (FONT3's read as H and Π at 1x) ================= */
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep blue over the hills, a warm haze where the Ribeira's lamps are ================= */
const SKY = ['#04071a', '#060b22', '#08102b', '#0b1534', '#0e1a3d', '#121f46', '#17254e', '#1d2b55', '#24305a',
  '#2d355d', '#383a60', '#46405f', '#56465c'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 175), 1.55) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - 110) / 230, (y - 165) / 95)) * 0.2;     // the Ribeira's warm haze
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 70, (y - MOON.y) / 50)) * 0.12; // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.6], [1, 2, 1.4], [2, -2, 1], [-1, 3, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fffbea' : '#f2ead2';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c8bc9e' : '#e6dcc0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#bfb498' : '#d6ccb0';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(31);
  while (STARS.length < 46) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 96);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: y < 50 ? 0.45 + r() * 0.5 : 0.25 + r() * 0.35, big: r() < 0.1 });
  }
}
// thin moonlit wisps (no outlines, dithered into the sky); tileable so phase 2 can drift them
const WISPS = [{ seed: 3, y: 4, h: 14, n: 2, k: 1 }, { seed: 5, y: 38, h: 16, n: 2, k: 2 }, { seed: 9, y: 64, h: 14, n: 2, k: 1 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 50 + r() * 70;
    for (let k = 0; k < 6; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.4 + r() * 0.25), rx: 12 + r() * 16, ry: 1.6 + r() * 1.8 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v > 0.55) p.put(x, y, v > 0.8 ? '#28325e' : '#202a56');
    else if (v > 0.2 && (v - 0.2) * 2.6 > bayer(x, y)) p.put(x, y, '#1a2350');
  }
  return { c: p.done(), y: def.y, k: def.k };
}

// the Douro's autumn mist: a low, broken band lying on the water along the far bank, drifting downriver (tileable)
function renderMist() {
  const h = 20, p = painter(W, h), r = rng(44), blobs = [];
  for (let k = 0; k < 9; k++) blobs.push({ x: r() * W, y: 9 + r() * 5, rx: 22 + r() * 30, ry: 3 + r() * 3 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.15 && v * 0.75 > bayer(x, y) + 0.08) p.put(x, y, v > 0.6 ? '#4a5878' : '#3a4866');
  }
  return p.done();
}

/* ================= The upriver valley, seen through the arch ================= */
// Fontainhas' slope on the left, Gaia's on the right, dark against the haze, with a few lit windows; the river runs on
// to the east between them
function renderValley() {
  const p = painter(W, H);
  const north = x => 104 + (x - 196) * 0.36 + 3 * Math.sin(x * 0.21), south = x => 150 - (x - 300) * 0.28 + 2 * Math.sin(x * 0.17);
  for (let x = 196; x < 412; x++) {
    const yn = Math.round(north(x)), ys = Math.round(south(x)), top = Math.min(yn, ys);
    for (let y = Math.max(0, top); y < WL; y++) {
      const isN = y >= yn && (y < ys || x < 300);
      const v = (y - top) / 40 + (bayer(x, y) - 0.5) * 0.2;
      p.put(x, y, isN ? (v < 0.3 ? '#262848' : '#1e2140') : (v < 0.3 ? '#22253f' : '#1a1d36'));
    }
  }
  // blocks of houses on the slopes, each with a lit edge and a window or two
  const r = rng(77);
  for (let k = 0; k < 26; k++) {
    const onN = k < 15, x = onN ? 200 + Math.floor(r() * 120) : 300 + Math.floor(r() * 104);
    const g = Math.round(onN ? north(x) : south(x)) + 1 + Math.floor(r() * 10), w = 4 + Math.floor(r() * 5), h = 3 + Math.floor(r() * 4);
    if (g + h >= WL - 1) continue;
    for (let y = g; y < g + h; y++) for (let i = x; i < x + w; i++) p.put(i, y, i === x ? '#3a3a5a' : y === g ? '#34345a' : '#2a2c4c');
    if (r() < 0.75) p.put(x + 1 + Math.floor(r() * (w - 2)), g + 1 + Math.floor(r() * (h - 2)), r() < 0.8 ? '#e8a858' : '#f2d08a');
    if (r() < 0.4) p.put(x + 1 + Math.floor(r() * (w - 2)), g + 1 + Math.floor(r() * (h - 2)), '#c88a4a');
  }
  return p.done();
}

/* ================= Torre dos Clérigos: granite baroque tower, floodlit warm white ================= */
// Real: 75.6 m tall, about 11 m across its shaft (H:W about 6.9). The shaft up to the big cornice is half the height,
// the clock stage 18 %, the balustraded belvedere 10 %, the lantern, ball and cross 22 %.
const CLS = { hi: '#fff4dc', lt: '#f0dcb4', md: '#cdb48e', sh: '#9c8468', dk: '#6a5848', ink: '#3a3038' };
function renderClerigos() {
  const p = painter(W, H), { x: cx, tip, base } = CL, put = p.put;
  const band = (y0, y1, hw, lit = 0) => {                // a stage of the tower: half-width hw, lit from the left
    for (let y = y0; y <= y1; y++) for (let i = cx - hw; i <= cx + hw; i++) {
      const u = (i - (cx - hw)) / (2 * hw), v = u + (bayer(i, y) - 0.5) * 0.18 - lit;
      put(i, y, i === cx - hw ? CLS.hi : i === cx + hw ? CLS.sh : v < 0.3 ? CLS.lt : v < 0.78 ? CLS.md : CLS.sh);
    }
  };
  const ledge = (y, hw) => { for (let i = cx - hw; i <= cx + hw; i++) { put(i, y, i <= cx ? CLS.hi : CLS.lt); put(i, y + 1, CLS.dk); } };
  // shaft: four storeys with corner pilasters, a door, a niche, the bell opening and the escutcheon under the cornice
  band(66, base, 5);
  for (const y of [94, 84, 75]) ledge(y, 5);
  for (let y = 67; y <= base; y++) { put(cx - 4, y, CLS.lt); put(cx + 4, y, CLS.dk); }
  for (let y = 97; y <= 102; y++) for (const i of [cx - 1, cx, cx + 1]) put(i, y, y === 97 && i !== cx ? CLS.md : '#4a3a34');
  for (let y = 87; y <= 90; y++) put(cx, y, y === 87 ? CLS.md : '#58483e');
  for (let y = 77; y <= 82; y++) for (const i of [cx - 1, cx, cx + 1]) put(i, y, y === 77 && i !== cx ? CLS.md : i === cx && y > 79 ? '#8a6a3a' : '#3a2e2e');
  put(cx, 79, '#b08a4a');                                  // the bell catching the light
  for (const [i, y] of [[cx - 1, 69], [cx, 68], [cx + 1, 69], [cx, 70], [cx - 1, 71], [cx + 1, 71], [cx, 72]]) put(i, y, CLS.hi);
  ledge(65, 6); ledge(64, 6);
  // clock stage: narrower, the clock face, urn finials on the corners
  band(52, 63, 4);
  for (let y = 53; y <= 63; y++) put(cx - 3, y, CLS.lt);
  for (const [i, y] of [[cx - 1, 56], [cx, 55], [cx + 1, 56], [cx, 57], [cx - 1, 57], [cx + 1, 57], [cx, 56]]) put(i, y, '#f8f0dc');
  put(cx, 56, CLS.ink); put(cx, 55, '#d8ccb0');
  for (const s of [-1, 1]) { put(cx + s * 5, 63, CLS.md); put(cx + s * 5, 62, CLS.lt); put(cx + s * 5, 61, CLS.hi); }
  ledge(51, 5);
  // belvedere: balustrade on the cornice, an arched opening each side, finials
  band(46, 50, 3);
  for (let i = cx - 4; i <= cx + 4; i++) { put(i, 50, (i & 1) ? CLS.lt : CLS.dk); put(i, 49, CLS.hi); }
  put(cx, 47, '#4a3a34'); put(cx, 48, '#4a3a34');
  for (const s of [-1, 1]) { put(cx + s * 4, 48, CLS.lt); put(cx + s * 4, 47, CLS.hi); }
  ledge(45, 4);
  // crown: the lantern with its finials, the cupola, the ball and the iron cross
  band(40, 44, 2);
  put(cx, 42, '#4a3a34'); put(cx, 43, '#4a3a34');
  for (const s of [-1, 1]) { put(cx + s * 3, 44, CLS.md); put(cx + s * 3, 43, CLS.lt); put(cx + s * 3, 42, CLS.hi); }
  for (let i = cx - 2; i <= cx + 2; i++) put(i, 39, i <= cx ? CLS.hi : CLS.md);
  for (let i = cx - 1; i <= cx + 1; i++) put(i, 38, i < cx ? CLS.hi : CLS.lt);
  put(cx, 37, CLS.lt); put(cx, 36, CLS.md);
  put(cx - 1, 34, CLS.lt); put(cx, 34, CLS.hi); put(cx + 1, 34, CLS.md);
  put(cx - 1, 35, CLS.md); put(cx, 35, CLS.lt); put(cx + 1, 35, CLS.sh); put(cx, 33, CLS.lt);
  for (let y = tip; y <= 32; y++) put(cx, y, '#8a7a6a');
  put(cx - 1, tip + 1, '#8a7a6a'); put(cx + 1, tip + 1, '#8a7a6a');
  return p.done();
}

/* ================= The Sé and the bishop's palace on the hilltop ================= */
// The cathedral's two square towers, battlemented, each under a small ribbed cupola; the bishop's palace, a long
// baroque front, to its right above where the bridge's upper deck comes into the hill. Both floodlit, a step under the
// Clérigos tower.
const SEC = { lt: '#d8c4a0', md: '#b09a7a', sh: '#7e6c5a', dk: '#4a4048', roof: '#5a3a30' };
function renderSe(p) {
  const put = p.put, { x0, x1, crest } = SE;
  // the terrace the Sé stands on: a granite retaining wall with buttresses over the Barredo's roofs
  for (let y = crest; y < crest + 12; y++) for (let i = x0 - 6; i <= x1 + 26; i++) {
    const but = (i - x0 + 6) % 11 < 2, v = (y - crest) / 12 + (bayer(i, y) - 0.5) * 0.3;
    put(i, y, y === crest ? '#c8b294' : but ? (i % 11 === 0 ? '#a8957c' : '#6e6058') : v < 0.4 ? '#7e6e62' : v < 0.8 ? '#62564e' : '#4a4044');
  }
  // nave between the towers, its gable with the rose window and the baroque porch
  for (let y = 70; y < crest; y++) for (let i = x0 + 8; i <= x1 - 8; i++) put(i, y, y < 72 ? SEC.lt : i < x0 + 12 ? SEC.lt : SEC.md);
  for (let k = 0; k < 4; k++) for (let i = x0 + 9 + k; i <= x1 - 9 - k; i++) put(i, 69 - k, k === 3 ? SEC.lt : SEC.md);
  for (const [i, y] of [[0, -1], [-1, 0], [1, 0], [0, 1], [0, 0]]) put(165 + i, 75 + y, i || y ? '#e8c27a' : '#f6dca0');
  for (let y = 79; y < crest; y++) for (let i = 163; i <= 167; i++) put(i, y, y === 79 || i === 163 || i === 167 ? SEC.lt : '#5a4038');
  // two towers with battlements and cupolas
  for (const tx of [x0, x1 - 7]) {
    for (let y = 62; y < crest; y++) for (let i = tx; i <= tx + 7; i++)
      put(i, y, i === tx ? '#eadcbc' : i === tx + 7 ? SEC.sh : (y % 7 === 0) ? SEC.md : (i - tx) < 4 ? SEC.lt : SEC.md);
    for (let i = tx; i <= tx + 7; i += 2) put(i, 61, SEC.lt);
    for (let y = 66; y <= 70; y++) { put(tx + 3, y, '#3a2e30'); put(tx + 4, y, '#3a2e30'); }
    put(tx + 3, 65, SEC.md); put(tx + 4, 65, SEC.md);
    [6, 6, 6, 4, 2].forEach((w, k) => { for (let i = tx + 4 - (w >> 1); i < tx + 4 + (w >> 1); i++) put(i, 60 - k, k === 2 && (i - tx) % 2 ? SEC.sh : (i - tx) < 4 ? SEC.lt : SEC.md); });
    put(tx + 3, 55, SEC.lt); put(tx + 3, 54, SEC.md);
  }
  // the bishop's palace: three storeys of tall windows, a balustrade, lit cream
  for (let y = 75; y < 86; y++) for (let i = 184; i <= 207; i++) {
    let col = i === 184 ? '#e6dcc4' : y === 75 ? '#ece2c8' : y === 76 ? SEC.sh : (i > 203 ? '#a89c86' : '#c8bca2');
    if (y >= 78 && (y - 78) % 3 < 2 && (i - 186) % 3 === 0 && i < 206) col = hash(i, y, 4) < 0.45 ? '#f2c878' : '#3a3440';
    put(i, y, col);
  }
  for (let i = 185; i <= 206; i += 2) put(i, 74, '#d8ccb0');
}

/* ================= The Ribeira: tall narrow houses stacked up the hill ================= */
const NIGHT = '#141a3a';
const FAC = [                                                  // facade ramps, lamplit to shadowed
  ['#e8b456', '#c89038', '#966a2a'], ['#d8684a', '#b04834', '#7e3226'], ['#6a90d4', '#4a70b0', '#30508a'],
  ['#ead8b6', '#c6b292', '#928470'], ['#dca098', '#b87e7a', '#86585a'], ['#72aa86', '#548a6a', '#3a644e'],
  ['#eee8dc', '#c8c2b6', '#928e88'], ['#e4904a', '#bc7034', '#8a5026'],
];
const TILED = new Set([2, 5]);                                 // azulejo-fronted houses
const ROOFS = ['#c45a34', '#963e24', '#622a1c'];
const WARMW = ['#ffd890', '#f6bc66', '#ec9c4c'];
// tiers back to front: [x0, x1, top range, storey height, window size, night mix]
const TIERS = [
  { x0: -4, x1: 152, top: [92, 101], st: 4, win: 1, dk: 0.5, base: 118, seed: 11 },
  { x0: -6, x1: 180, top: [103, 114], st: 5, win: 2, dk: 0.3, base: 132, seed: 23 },
  { x0: -8, x1: 192, top: [116, 128], st: 6, win: 2, dk: 0.1, base: 152, seed: 37 },
];
const HOUSES = [];
function layoutHouses() {
  TIERS.forEach((T, ti) => {
    const r = rng(T.seed);
    let x = T.x0, prev = -1;
    while (x < T.x1) {
      const w = (ti === 2 ? 9 : ti === 1 ? 8 : 7) + Math.floor(r() * (ti === 2 ? 7 : 5));
      let f; do f = Math.floor(r() * FAC.length); while (f === prev);
      prev = f;
      let top = T.top[0] + Math.floor(r() * (T.top[1] - T.top[0]));
      // the hill rises from the west towards the Sé; below the Sé (the Barredo) the houses climb the steep slope to it
      if (ti === 0) top += 5 - Math.round(x * 0.06);
      if (ti === 1) top += 4 - Math.round(x * 0.05);
      if (ti === 2 && x > 150) top -= Math.round((x - 150) * 0.45);
      if (ti === 1 && x > 148) top -= Math.round((x - 148) * 0.3);
      HOUSES.push({ x, w: Math.min(w, T.x1 + 6 - x), top, base: T.base, f, ti, st: T.st, win: T.win, dk: T.dk, seed: x * 7 + ti,
        dormer: r() < 0.3, laundry: ti === 2 && r() < 0.3, balcony: r() < 0.55 });
      x += w;
    }
  });
}
function drawHouse(p, h) {
  const put = p.put, F = FAC[h.f].map(c => mixHex(c, NIGHT, h.dk)), RF = ROOFS.map(c => mixHex(c, NIGHT, h.dk + 0.1));
  const win = WARMW.map(c => mixHex(c, '#3a2030', h.dk * 0.5)), dark = mixHex('#1a1c34', NIGHT, h.dk);
  const { x, w, top, base, st } = h, front = h.ti === 2, ground = front ? base - 9 : base;
  for (let y = top; y < base; y++) for (let i = x; i < x + w; i++) {
    const v = clamp((y - top) / Math.max(1, base - top), 0, 1) * (front ? 0.75 : 0.55) + 0.25 + (bayer(i, y) - 0.5) * 0.22;
    let col = i === x ? F[0] : i === x + w - 1 ? F[2] : v > 0.62 ? F[0] : v > 0.36 ? F[1] : F[2];
    if (TILED.has(h.f) && ((i + y) & 1) === 0 && i > x && i < x + w - 1) col = mixHex(col, '#e8eef8', 0.18);
    if (y === top) col = mixHex('#efe2c4', NIGHT, h.dk);     // granite cornice
    put(i, y, col);
  }
  // roof: hipped tiles over the cornice, a dormer now and then
  for (let k = 1; k <= 2; k++) for (let i = x + k - 1; i <= x + w - k; i++) put(i, top - k, k === 1 ? RF[1] : RF[2]);
  for (let i = x; i < x + w; i++) if (hash(i, top, 9) < 0.5) put(i, top - 1, RF[0]);
  if (h.dormer && w >= 9) { const dx = x + (w >> 1) - 1; for (let i = dx; i < dx + 3; i++) { put(i, top - 3, RF[1]); put(i, top - 4, RF[2]); } put(dx + 1, top - 3, hash(h.seed, 1) < 0.6 ? win[0] : dark); }
  // windows: tall sashes in columns, storey by storey; some lit, some dark, a few with green shutters
  const cols = Math.max(1, Math.floor((w - 2) / (h.win + 2))), gap = (w - cols * h.win) / (cols + 1);
  for (let y = top + 3, s = 0; y + h.win + 1 < ground; y += st, s++) {
    for (let c = 0; c < cols; c++) {
      const wx = Math.round(x + gap + c * (h.win + gap)), toggle = hash(h.seed, s, c + 20) < 0.38;
      const lit = !toggle && hash(h.seed, s, c) < (front ? 0.5 : 0.42), wh = h.win + 1;
      const on = win[Math.floor(hash(h.seed, s, c + 9) * 3)], col = lit ? on : dark;
      if (toggle) WINS.push({ id: WINS.length, x: wx, y, w: h.win, h: wh, on, top: win[0], n: 2 + Math.floor(hash(h.seed, s, c + 30) * 5), ph: hash(h.seed, s, c + 40) });
      for (let j = 0; j < wh; j++) for (let k = 0; k < h.win; k++) put(wx + k, y + j, j === 0 && lit ? win[0] : col);
      if (!lit && front && hash(h.seed, s, c + 5) < 0.4) { put(wx - 1, y, '#2c4a3a'); put(wx + h.win, y, '#2c4a3a'); }
    }
    if (front && h.balcony && s > 0) for (let i = x + 1; i < x + w - 1; i++) put(i, y + h.win + 1, (i & 1) ? '#1a1820' : '#2c2a34');
  }
  if (front && h.laundry) {                                    // a line of washing under one window
    const ly = top + 3 + st * 2 + h.win + 2;
    for (let i = x + 1; i < x + w - 1; i++) {
      put(i, ly, '#3a3440');
      if (i % 3 !== 0) put(i, ly + 1, ['#e8e2d6', '#c84a4a', '#6a8ad8', '#e8c050'][(i + h.seed) % 4]);
    }
  }
}
// the Cais da Ribeira: the front houses stand on arcades lit from inside; the granite quay wall below, steps, lamps
function renderQuayFront(p) {
  const put = p.put;
  for (const h of HOUSES) if (h.ti === 2) {
    for (let y = h.base - 9; y < h.base; y++) for (let i = h.x; i < h.x + h.w; i++) put(i, y, i === h.x ? '#8e7a66' : '#6a5a50');
    for (let ax = h.x + 2; ax + 3 < h.x + h.w; ax += 6) for (let y = h.base - 7; y < h.base; y++) for (let i = ax; i < ax + 4; i++) {
      if (y === h.base - 7 && (i === ax || i === ax + 3)) continue;
      const v = (y - (h.base - 7)) / 7 + (bayer(i, y) - 0.5) * 0.3;
      put(i, y, v < 0.35 ? '#ffd690' : v < 0.7 ? '#f0a85a' : '#c87a3e');
    }
  }
  for (let y = 152; y < WL; y++) for (let x = -2; x < 196; x++) {
    let col = y === 152 ? '#a89480' : y === 153 ? '#7a6a5e' : (y % 3 === 0 || (x + (Math.floor(y / 3) & 1) * 4) % 8 === 0) ? '#3a3440' : '#4e4650';
    if (y > 155 && ((x * 7) % 61) < 6) col = y === 156 ? '#6a5c54' : '#56484a';     // steps down to the water
    put(x, y, col);
  }
}
// the Muralha Fernandina: a stretch of the old granite wall stepping down beside the bridge's north end
function renderWall(p) {
  for (let k = 0; k < 6; k++) {
    const x0 = 158 + k * 5, y0 = 112 + k * 7;
    for (let y = y0; y < y0 + 8; y++) for (let i = x0; i < x0 + 6; i++) p.put(i, y, i === x0 ? '#8e7e6c' : y === y0 ? '#9a8a76' : '#5e5250');
    p.put(x0 + 1, y0 - 1, '#8e7e6c'); p.put(x0 + 4, y0 - 1, '#8e7e6c');
  }
}
function renderCity() {
  const p = painter(W, H), put = p.put;
  // the hill behind the houses, so no sky shows between them
  for (let x = 0; x < 200; x++) {
    const top = x < 150 ? 98 - x * 0.06 : x < 186 ? 86 : 86 + (x - 186) * 4;
    for (let y = Math.round(top); y < WL; y++) put(x, y, (bayer(x, y) < 0.3) ? '#1c1c36' : '#181830');
  }
  renderSe(p);
  for (const h of HOUSES) if (h.ti === 0) drawHouse(p, h);
  for (const h of HOUSES) if (h.ti === 1) drawHouse(p, h);
  renderWall(p);
  for (const h of HOUSES) if (h.ti === 2) drawHouse(p, h);
  renderQuayFront(p);
  return p.done();
}

/* ================= Gaia's bluff and the Serra do Pilar monastery ================= */
const BLUFF = [[396, 160], [406, 150], [413, 138], [420, 124], [428, 110], [436, 96], [443, 84], [450, 75], [458, 71], [480, 69]];
const bluffY = x => { for (let k = 1; k < BLUFF.length; k++) { const [a, ya] = BLUFF[k - 1], [b, yb] = BLUFF[k]; if (x <= b) return lerp(ya, yb, clamp((x - a) / (b - a), 0, 1)); } return 69; };
function renderBluff() {
  const p = painter(W, H), put = p.put;
  for (let x = 396; x < W; x++) for (let y = Math.round(bluffY(x)); y < WL; y++) {
    const n = hash(x >> 1, y >> 1, 61), edge = y - bluffY(x) < 2;
    put(x, y, edge ? '#2e3a36' : n < 0.25 ? '#22302c' : n < 0.6 ? '#1c2228' : '#20202e');
  }
  // the round church: a drum under a dome and lantern, and the long cloister range to its left, floodlit amber
  const SP = ['#e2b86c', '#c09450', '#94703c', '#644a30'];
  for (let y = 62; y < 74; y++) for (let x = 440; x < 458; x++) {
    let col = y === 62 ? SP[0] : x === 440 ? SP[0] : x > 455 ? SP[2] : SP[1];
    if (y >= 65 && y <= 70 && (x - 442) % 3 === 0 && x < 456) col = hash(x, y, 3) < 0.4 ? '#ffd890' : '#3a2e30';
    put(x, y, col);
  }
  const cx = 468;
  for (let y = 56; y < 74; y++) for (let x = cx - 10; x <= cx + 10; x++) {
    const u = (x - (cx - 10)) / 20;
    let col = u < 0.15 ? SP[0] : u < 0.55 ? SP[1] : u < 0.85 ? SP[2] : SP[3];
    if (y === 56 || y === 57) col = y === 56 ? SP[0] : SP[3];
    if (y >= 60 && y <= 66 && (x - cx + 9) % 4 === 1) col = SP[3];
    put(x, y, col);
  }
  for (let k = 0; k < 7; k++) {
    const hw = Math.round(9 * Math.sqrt(1 - (k / 7) ** 2));
    for (let x = cx - hw; x <= cx + hw; x++) put(x, 55 - k, x < cx - hw / 3 ? SP[0] : x < cx + hw / 2 ? SP[1] : SP[2]);
  }
  for (let y = 45; y < 49; y++) { put(cx - 1, y, SP[0]); put(cx, y, SP[1]); put(cx + 1, y, SP[2]); }
  put(cx, 44, SP[0]); put(cx, 43, SP[1]);
  return p.done();
}

/* ================= Dom Luís I: the iron arch, floodlit gold, and its two decks ================= */
// Real: the arch spans 172 m and rises 45 m (span:rise 3.8); the upper deck runs about 60 m over the river (0.35 of the
// span), resting on the arch's crown and on tall iron trestles at its feet; the lower deck is hung from the arch just
// above the water. Here: span 206 px, rise 54, upper deck 74 px over the river.
const GOLD = { hi: '#fff0b4', lt: '#ffd36a', md: '#e0a442', sh: '#a87430', dk: '#5e4026', back: '#7a5428', iron: '#2e2428' };
const yI = x => { const u = (x - BR.xc) / BR.hsI; return Math.abs(u) > 1 ? null : BR.yb - BR.riseI * (1 - u * u); };
const yE = x => { const u = (x - BR.xc) / BR.hsE; return Math.abs(u) > 1 ? null : BR.yb - BR.riseE * (1 - u * u); };
function inArch(x, y) {
  const e = yE(x);
  if (e === null || y < e - 0.01 || y > BR.yb) return false;
  const i = yI(x);
  return i === null || y <= i + 0.01;
}
function renderBridge() {
  const p = painter(W, H), put = p.put, { xc, yb, hsE, top, bot, ltop, lbot, d0, d1, l0, l1 } = BR;
  // arc length along the arch, for the lattice's rhythm
  const S = new Float32Array(W);
  for (let x = xc - hsE + 1; x <= xc + hsE; x++) {
    const a = ((yE(x) ?? yb) + (yI(x) ?? yb)) / 2, b = ((yE(x - 1) ?? yb) + (yI(x - 1) ?? yb)) / 2;
    S[x] = S[x - 1] + Math.hypot(1, a - b);
  }
  // the hangers of the lower deck: thin rods from the arch's underside down to the deck
  for (let x = xc - 90; x <= xc + 90; x += 10) {
    const yi = Math.ceil(yI(x));
    for (let y = yi + 1; y < ltop; y++) if (y & 1) put(x, y, GOLD.dk);
  }
  // the arch: two chords and a lattice of diagonals between them; the rear rib's lattice shows dimmer through it
  const P = 9;
  for (let x = xc - hsE; x <= xc + hsE; x++) for (let y = Math.floor(yE(x) ?? yb); y <= yb; y++) {
    if (!inArch(x, y)) continue;
    const out = (a, b) => !inArch(a, b);
    const outer = (out(x, y - 1) && y - 1 < (yE(x) ?? yb)) || (out(x - 1, y) && x <= xc && (yE(x - 1) === null || y < yE(x - 1))) ||
      (out(x + 1, y) && x >= xc && (yE(x + 1) === null || y < yE(x + 1)));
    const edge = out(x, y - 1) || out(x, y + 1) || out(x - 1, y) || out(x + 1, y);
    let col = null;
    if (edge) col = outer ? (x < xc + 30 ? GOLD.hi : GOLD.lt) : GOLD.md;
    else {
      // a Warren truss: one diagonal per panel, leaning the other way in the next; the rear rib's, half a panel on
      const e = yE(x), b = yI(x) ?? yb, f = (y - e) / Math.max(1, b - e), sp = S[x] / P, k = Math.floor(sp), q = sp - k;
      const g = (k & 1) ? 1 - f : f, kb = Math.floor(sp + 0.5), qb = sp + 0.5 - kb, gb = (kb & 1) ? 1 - f : f;
      if (Math.abs(q - g) * P < 0.7 || q * P < 0.5) col = f < 0.5 ? GOLD.lt : GOLD.md;
      else if (Math.abs(qb - gb) * P < 0.6) col = GOLD.sh;
      else if (bayer(x, y) < 0.45) col = f < 0.5 ? GOLD.back : GOLD.dk;          // the far rib's plates, catching less light
    }
    if (col) put(x, y, col);
  }
  // iron posts on the arch carrying the upper deck
  for (const x of POSTS) {
    const e = Math.floor(yE(x));
    for (let y = bot + 1; y < e; y++) { put(x, y, GOLD.lt); put(x + 1, y, GOLD.sh); if ((y - bot) % 6 === 3) { put(x - 1, y, GOLD.md); put(x + 2, y, GOLD.dk); } }
  }
  // the trestles: tall tapering iron piers in three sections, X-braced, on the abutments and the hillside
  for (const T of TRESTLES) {
    const h = T.foot - bot, hw0 = (T.w - 1) / 2, hw1 = 2;
    const hw = y => lerp(hw1, hw0, (y - bot) / h);
    for (let y = bot + 1; y <= T.foot; y++) {
      const a = Math.round(T.x - hw(y)), b = Math.round(T.x + hw(y));
      put(a, y, GOLD.lt); put(b, y, GOLD.sh);
    }
    for (let k = 0; k < 3; k++) {
      const ya = Math.round(bot + 1 + k * h / 3), yb2 = Math.round(bot + 1 + (k + 1) * h / 3) - 1;
      bres(put, Math.round(T.x - hw(ya)) + 1, ya, Math.round(T.x + hw(yb2)) - 1, yb2, GOLD.md);
      bres(put, Math.round(T.x + hw(ya)) - 1, ya, Math.round(T.x - hw(yb2)) + 1, yb2, GOLD.sh);
      for (let i = Math.round(T.x - hw(yb2)); i <= Math.round(T.x + hw(yb2)); i++) put(i, yb2 + 1, GOLD.lt);
    }
  }
  // the granite abutments: a river step where the arch bears, a taller landward pier carrying the lower deck
  const granite = (x0, x1, y0, lit) => {
    for (let y = y0; y < WL; y++) for (let x = x0; x <= x1; x++) {
      const v = (y - y0) / 14 + (bayer(x, y) - 0.5) * 0.25;
      let col = y === y0 ? '#e8cfa0' : (y - y0) % 4 === 0 || (x + ((y - y0) >> 2) * 3) % 7 === 0 ? '#6a5446' : v < 0.45 ? '#c4a07a' : v < 0.85 ? '#9a7c62' : '#6e5a50';
      if (x === (lit ? x0 : x1)) col = lit ? '#e2c49a' : '#5a4842';
      put(x, y, col);
    }
  };
  granite(xc - 113, xc - 95, yb, true); granite(xc + 95, xc + 113, yb, false);
  granite(l0 - 10, xc - 113, ltop - 2, true); granite(xc + 113, l1 + 2, ltop - 2, false);
  // the lower deck: a lattice girder with a lit rail and its lamps
  for (let x = l0; x <= l1; x++) {
    put(x, ltop, GOLD.lt); put(x, lbot, GOLD.md); put(x, lbot + 1, GOLD.iron);
    for (let y = ltop + 1; y < lbot; y++) { const q = (x + (y - ltop)) % 5, r = (x - (y - ltop) + 100) % 5; if (!q || !r) put(x, y, GOLD.sh); else if (y === ltop + 1) put(x, y, GOLD.dk); }
    if ((x - l0) % 12 === 6) { put(x, ltop - 1, GOLD.dk); put(x, ltop - 2, '#fff2c8'); }
  }
  // the upper deck: top chord with its row of lamps, lattice, bottom chord resting on the crown
  for (let x = d0; x <= d1; x++) {
    put(x, top, GOLD.hi); put(x, top + 1, GOLD.lt);
    for (let y = top + 2; y < bot; y++) { const q = (x + (y - top)) % 6, r = (x - (y - top) + 120) % 6; put(x, y, !q || !r ? GOLD.md : y === top + 2 ? GOLD.dk : null); }
    put(x, bot, GOLD.md); put(x, bot - 1, (x & 1) ? GOLD.sh : GOLD.dk);
    if ((x - d0) % 8 === 4) { put(x, top - 1, GOLD.dk); put(x, top - 2, '#fff6d8'); }
    else if ((x - d0) % 2 === 0) put(x, top - 1, GOLD.sh);           // the parapet's railing
  }
  // the deck's ends on granite piers: into the hill under the Sé, into Gaia's bluff
  for (const [x0, x1] of [[d0 - 3, d0 + 3], [d1 - 4, d1 + 2]]) for (let y = top - 1; y < bot + 9; y++) for (let x = x0; x <= x1; x++)
    put(x, y, y === top - 1 ? '#e8cfa0' : x === x0 ? '#c4a07a' : (y % 3 === 0) ? '#5a4842' : '#8a7060');
  return p.done();
}

/* ================= The Douro ================= */
const renderWater = () => ditherFill(W, KERB - WL + 4, ['#123038', '#0e262e', '#0b1e26', '#09171e', '#071218'], (i, y) => y / (KERB - WL + 4) * 0.9 + 0.1);

/* ================= Rabelo boats moored off the quay ================= */
// the flat-bottomed Douro boats that brought the port down from the valley: a long black hull sweeping up at both
// ends, the raised steering platform (apegada) at the stern with its long oar, a row of pipes of port, one mast with a
// square sail on its yard
const RAB = [
  { x: 62, wl: 194, len: 88, d: 8, mast: 52, sail: 'furled', stripe: '#c83a32', seed: 3 },
  { x: 224, wl: 182, len: 60, d: 6, mast: 38, sail: 'set', stripe: '#e8c24a', seed: 8 },
];
function renderRabelo(R) {
  const w = R.len + 26, h = R.mast + R.d + 14, p = painter(w, h), put = p.put, wl = h - 3, x0 = 3, d = R.d;
  const hullTop = u => wl - d - Math.round(9 * Math.pow(Math.max(0, 1 - u / 0.16), 2) + 6 * Math.pow(Math.max(0, (u - 0.86) / 0.14), 2));
  for (let i = 0; i < R.len; i++) {
    const u = i / (R.len - 1), t0 = hullTop(u), keel = wl - (u < 0.05 || u > 0.95 ? 2 : u < 0.1 || u > 0.9 ? 1 : 0);
    for (let y = t0; y <= keel; y++) {
      let col = y === t0 ? '#9a7650' : y === t0 + 1 ? '#3a2a26' : y === t0 + 2 ? R.stripe : y === t0 + 3 ? '#e8e0cc' : '#16121a';
      if (y > t0 + 3 && y < keel && bayer(i, y) < 0.12 + 0.3 * (1 - u)) col = '#2a2430';     // lamplight on the bow's planks
      put(x0 + i, y, col);
    }
  }
  // pipes of port on deck, hooped
  const n = Math.round(R.len / 15), bw = d > 6 ? 7 : 6, bh = d > 6 ? 6 : 5;
  for (let k = 0; k < n; k++) {
    const bx = x0 + Math.round(R.len * 0.2) + k * (bw + 1), by = wl - d - bh + 1;
    for (let y = by; y < by + bh; y++) for (let i = bx; i < bx + bw; i++) {
      if ((y === by || y === by + bh - 1) && (i === bx || i === bx + bw - 1)) continue;
      const hoop = y === by + 1 || y === by + bh - 2;
      put(i, y, hoop ? '#2a2026' : i === bx ? '#b88456' : i > bx + bw - 3 ? '#5a3a28' : '#8a5c38');
    }
  }
  // the stern platform (apegada) on its posts, the long steering oar trailing into the water
  const sx = x0 + Math.round(R.len * 0.8), py = wl - d - 9;
  for (let i = sx - 4; i <= sx + 8; i++) { put(i, py, '#b08a5c'); put(i, py + 1, '#3a2a26'); }
  for (const i of [sx - 3, sx + 7]) for (let y = py + 2; y < wl - d; y++) put(i, y, '#4a3a30');
  for (let i = sx - 3; i <= sx + 7; i += 2) put(i, py - 1, '#6a5a4e');
  bres(put, sx + 7, py - 1, x0 + R.len + 21, wl + 1, '#5e4636');
  bres(put, sx + 7, py - 2, x0 + R.len + 19, wl - 1, '#9a7650');
  // the mast, its yard and the square sail, furled along the yard or set
  const mx = x0 + Math.round(R.len * 0.45), top = wl - d - 1 - R.mast;
  for (let y = top; y < wl - d; y++) { put(mx, y, '#7a5e48'); put(mx + 1, y, '#2a2026'); }
  const yw = Math.round(R.mast * 0.44), yy = top + 3;
  for (let i = mx - yw; i <= mx + yw + 1; i++) put(i, yy, '#7a5e48');
  // stays from the masthead to bow and stern
  bres(put, mx, top + 1, x0 + 2, hullTop(0) - 1, '#4a3e3c');
  bres(put, mx + 1, top + 1, sx + 2, py - 1, '#4a3e3c');
  if (R.sail === 'furled') for (let i = mx - yw + 1; i <= mx + yw; i++) {
    const tie = (i - mx + 40) % 6 === 0;
    put(i, yy + 1, tie ? '#5a4a3e' : (i & 2) ? '#e2d6ba' : '#c4b698'); put(i, yy + 2, tie ? '#5a4a3e' : '#a89a80');
    if (Math.abs(i - mx) < yw - 2) put(i, yy + 3, hash(i, R.seed) < 0.5 ? '#8a7e6a' : '#a89a80');
  }
  else for (let y = yy + 1; y < yy + Math.round(R.mast * 0.62); y++) {
    const belly = Math.round(Math.sin(Math.PI * (y - yy) / (R.mast * 0.62)) * 2);
    for (let i = mx - yw + 1 + belly; i <= mx + yw - belly; i++) {
      const u = (i - (mx - yw)) / (2 * yw), band = Math.abs((y - yy) / (R.mast * 0.62) - 0.55) < 0.08;
      put(i, y, band ? '#a83a32' : u < 0.3 ? '#efe4c8' : u < 0.75 ? '#d6c8a8' : '#ae9e82');
    }
  }
  return { c: p.done(), ox: R.x - x0, oy: R.wl - wl };
}

/* ================= The Gaia quay: kerb, setts, bollards, the lamp ================= */
const BOLLARDS = [150, 214, 296, 350];
function renderQuay() {
  const p = painter(W, H), put = p.put;
  // the kerb: dressed granite blocks, their top worn bright, mooring rings
  for (let y = KERB; y < KERB + 4; y++) for (let x = 0; x < W; x++) {
    let col = y === KERB ? '#8e8a9a' : y === KERB + 1 ? '#6a6678' : (x % 13 === 0) ? '#2a2836' : '#4a4658';
    if (y === KERB + 3) col = '#24222e';
    put(x, y, col);
  }
  // granite setts in perspective (small near the kerb, larger towards the viewer), wet after the drizzle: rounded
  // stones, each catching a little light on its top edge, warm in the lamp's pool and in front of the lodge; the wet
  // stone mirrors the quay's lights (drawn per frame)
  const pool = (x, y) => Math.max(0, 1 - Math.hypot((x - LAMP.x) / 70, (y - 246) / 26)) * 0.9 + Math.max(0, 1 - Math.hypot((x - 452) / 80, (y - 214) / 22));
  for (let y = KERB + 4, row = 0; y < H; row++) {
    const sh = 2 + Math.min(4, Math.floor(row / 2.6)), sw = 4 + Math.min(7, Math.floor(row * 0.7)), off = Math.floor(hash(row, 3, 17) * sw);
    for (let yy = y; yy < Math.min(H, y + sh); yy++) for (let x = 0; x < W; x++) {
      const k = Math.floor((x + off) / sw), lx = (x + off) % sw, ly = yy - y, n = hash(k, row, 17), w = pool(x, yy);
      const corner = (lx === 1 || lx === sw - 1) && (ly === 0 || ly === sh - 1);
      const joint = lx === 0 || (ly === 0 && n < 0.85) || corner;
      let v = (n - 0.5) * 0.35 + w * 0.9 + (bayer(x, yy) - 0.5) * 0.2;
      if (!joint && ly === 1) v += lx < sw / 2 ? 0.4 : 0.22;            // wet light on each stone's top edge
      if (!joint && ly === sh - 1 && sh > 2) v -= 0.18;
      const col = joint ? (w > 0.5 ? '#2a2026' : '#17171f') : v > 0.95 ? '#6a5446' : v > 0.6 ? '#4a3e40' : v > 0.28 ? '#34323f' : v > -0.04 ? '#2a2a38' : '#22222e';
      put(x, yy, col);
    }
    y += sh;
  }
  // iron bollards on the kerb
  for (const bx of BOLLARDS) for (let y = KERB - 6; y < KERB + 1; y++) for (let i = bx - 2; i <= bx + 2; i++) {
    if (y === KERB - 6 && (i === bx - 2 || i === bx + 2)) continue;
    put(i, y, y === KERB - 6 ? '#5a5a6a' : y === KERB - 5 ? '#2a2a34' : i === bx - 2 ? '#4a4a58' : '#1a1a22');
  }
  return p.done();
}
// the lodge terrace's deck (drawn under the people on it)
function renderDeck() {
  const p = painter(W, H), put = p.put;
  // the lodge's terrace: a timber deck a step up from the quay, planters of geraniums at its corners
  for (let y = LODGE.floor; y < LODGE.floor + 9; y++) for (let x = DECK.x0; x < W; x++) {
    const top = y === LODGE.floor, edge = y === LODGE.floor + 1;
    put(x, y, top ? '#c08a56' : edge ? '#6a4630' : (x - DECK.x0) % 6 === 0 ? '#2a1a16' : y < LODGE.floor + 4 ? '#4e3424' : '#3a281e');
  }
  for (let y = LODGE.floor - 4; y < LODGE.floor; y++) for (let x = DECK.x0; x < W; x++) put(x, y, (x + y) % 5 === 0 ? '#7a5636' : '#8e6844');
  for (const bx of [DECK.x0, DECK.x0 + 52]) {
    for (let y = LODGE.floor - 9; y < LODGE.floor + 1; y++) for (let x = bx; x < bx + 9; x++) put(x, y, y === LODGE.floor - 9 ? '#9a7a5a' : x === bx ? '#8a6a4a' : '#5a4030');
    for (let k = 0; k < 16; k++) {
      const fx = bx + Math.floor(hash(k, bx, 1) * 9), fy = LODGE.floor - 10 - Math.floor(hash(k, bx, 2) * 5);
      put(fx, fy, hash(k, bx, 3) < 0.45 ? '#d83a3a' : hash(k, bx, 4) < 0.5 ? '#2a6a3a' : '#3a8a4a');
    }
  }
  return p.done();
}
// what stands on the quay near the viewer, each on its own layer so the people walk in front of and behind it (feet
// order): the cast-iron lamp, the river-cruise kiosk, the granite bench, a mooring bollard, a table of the lodge's
// terrace brought out onto the setts, the old pipes of port; and a puddle holding the bridge's gold
const KIOSK = { x0: 52, x1: 108, top: 221, base: 263 };
const NEAR_TABLE = { x: 405, base: 262 };
const NB = { x: 138, y: 240 };                         // the mooring bollard near the viewer
const PIGEONS = [[296, 0.1], [306, 0.45], [318, 0.8]];
function renderLampPost() {
  const p = painter(W, H), put = p.put, { x: lx, y: ly } = LAMP, base = 266;
  // fluted base, collars, the post, a scrolled bracket under the lantern
  for (let y = base - 18; y <= base; y++) {
    const hw = y > base - 4 ? 5 : y > base - 10 ? 4 : 3;
    for (let i = lx - hw; i <= lx + hw; i++) put(i, y, i === lx - hw ? '#4a4a5c' : (i - lx + 8) % 2 ? '#1c1c26' : '#2a2a38');
  }
  for (const cy of [base - 18, base - 10, base - 4]) for (let i = lx - 5; i <= lx + 5; i++) if (Math.abs(i - lx) <= (cy === base - 18 ? 4 : 5)) put(i, cy, '#565668');
  for (let y = ly + 14; y < base - 18; y++) { put(lx - 1, y, '#3e3e4e'); put(lx, y, '#22222c'); put(lx + 1, y, '#121218'); }
  for (const cy of [ly + 40, ly + 70]) for (let i = lx - 2; i <= lx + 2; i++) put(i, cy, '#4a4a5c');
  for (const s of [-1, 1]) { put(lx + s * 3, ly + 16, '#2a2a36'); put(lx + s * 4, ly + 15, '#2a2a36'); put(lx + s * 4, ly + 14, '#2a2a36'); put(lx + s * 2, ly + 17, '#2a2a36'); }
  // the lantern: a crown with a finial, four panes of warm glass, a cone under it
  for (let i = lx - 6; i <= lx + 6; i++) put(i, ly - 1, '#2a2a36');
  for (let k = 0; k < 3; k++) for (let i = lx - 5 + k * 2; i <= lx + 5 - k * 2; i++) put(i, ly - 2 - k, k === 0 ? '#3a3a48' : '#24242e');
  put(lx, ly - 5, '#3a3a48'); put(lx, ly - 6, '#4a4a5a'); put(lx, ly - 7, '#2a2a36');
  for (let y = ly; y <= ly + 10; y++) for (let i = lx - 5 + ((y - ly) >> 3); i <= lx + 5 - ((y - ly) >> 3); i++) {
    const frame = Math.abs(i - lx) >= 5 - ((y - ly) >> 3) || i === lx;
    put(i, y, frame ? '#1a1a22' : y < ly + 3 ? '#fff8e0' : y < ly + 7 ? '#ffe0a0' : '#f4b860');
  }
  for (let k = 0; k < 3; k++) for (let i = lx - 3 + k; i <= lx + 3 - k; i++) put(i, ly + 11 + k, '#22222c');
  return p.done();
}
function renderKiosk() {
  const p = painter(W, H), put = p.put, { x0, x1, top, base } = KIOSK;
  const G = { hi: '#3e8a6a', lt: '#2e6e54', md: '#235842', sh: '#183e30', dk: '#0e261e' }, TRIM = '#d8c8a0';
  // a shallow zinc roof with a ridge finial, overhanging the box
  for (let k = 0; k < 8; k++) for (let x = x0 - 4 + k; x <= x1 + 4 - k; x++) {
    const y = top + 7 - k;
    put(x, y, k === 0 ? '#2a2c38' : k === 1 ? '#6a7080' : (x + k) % 4 === 0 ? '#3a3e4c' : x < (x0 + x1) / 2 ? '#525868' : '#444a58');
  }
  put((x0 + x1) >> 1, top - 1, '#6a7080'); put((x0 + x1) >> 1, top - 2, '#8a90a0');
  // the box: panelled green, the fascia with its name, the lit window and counter, the lower panels
  for (let y = top + 8; y < base; y++) for (let x = x0; x <= x1; x++) {
    let col = x === x0 ? G.hi : x >= x1 - 3 ? (x === x1 ? G.dk : G.sh) : G.md;
    if (y === top + 8 || y === top + 16) col = TRIM;
    else if (y > top + 8 && y < top + 16) col = x === x0 ? G.lt : G.dk;                         // fascia
    else if (y >= top + 18 && y <= top + 31 && x >= x0 + 5 && x <= x1 - 7) {                    // window
      const v = (y - top - 18) / 13 + (bayer(x, y) - 0.5) * 0.25;
      col = v < 0.3 ? '#ffe2a6' : v < 0.7 ? '#f6c070' : '#e09a50';
    } else if (y === top + 32 || y === top + 33) col = y === top + 32 ? '#c89a62' : '#6a4a30';    // counter
    else if (y > top + 34 && ((x - x0) % 12 === 2 || y === top + 35 || y === base - 2)) col = G.sh; // panel mouldings
    put(x, y, col);
  }
  // inside the window: posters of the river trips, a rack of leaflets, the window's frame
  for (const [px0, c1, c2] of [[x0 + 7, '#2a6ab0', '#e8e0c8'], [x0 + 38, '#c84a3a', '#f0d890']]) for (let y = top + 19; y < top + 26; y++) for (let x = px0; x < px0 + 6; x++)
    put(x, y, y === top + 23 ? c2 : y > top + 23 ? '#2a3a6a' : c1);
  for (let y = top + 18; y <= top + 31; y++) { put(x0 + 4, y, G.dk); put(x1 - 6, y, G.dk); }
  for (let x = x0 + 4; x <= x1 - 6; x++) put(x, top + 17, G.dk);
  for (let y = base; y < base + 2; y++) for (let x = x0 - 1; x <= x1 + 3; x++) put(x, y, '#121218');
  const c = p.done();
  sign(p.x, 'CRUZEIROS', ((x0 + x1 - signWidth('CRUZEIROS')) >> 1) - 1, top + 10, '#f2e4c0');
  sign(p.x, '6 PONTES', ((x0 + x1 - signWidth('6 PONTES')) >> 1) - 1, top + 37, '#e8c46a');
  return c;
}
function renderBench() {
  const p = painter(W, H), put = p.put;
  for (let y = BENCH.y; y < BENCH.y + 7; y++) for (let x = BENCH.x0; x <= BENCH.x1; x++) {
    if (y >= BENCH.y + 3 && x > BENCH.x0 + 3 && x < BENCH.x1 - 3) { put(x, y, '#121218'); continue; }
    put(x, y, y === BENCH.y ? '#9a94a2' : y === BENCH.y + 1 ? '#6a6676' : x === BENCH.x0 ? '#5a5666' : '#3e3c4a');
  }
  return p.done();
}
function renderNearBollard() {
  const p = painter(W, H), put = p.put;
  for (let y = NB.y; y < NB.y + 13; y++) for (let x = NB.x - 4; x <= NB.x + 4; x++) {
    const capRow = y < NB.y + 3, hw = capRow ? 4 : 3;
    if (Math.abs(x - NB.x) > hw || (y === NB.y && Math.abs(x - NB.x) === 4)) continue;
    put(x, y, y === NB.y ? '#6a6a7a' : capRow ? (x < NB.x - 1 ? '#4a4a58' : '#26262e') : x === NB.x - 3 ? '#3e3e4a' : x > NB.x + 1 ? '#121218' : '#1e1e26');
  }
  for (let i = 0; i < 3; i++) for (let a = 0; a < 26; a++) {
    const ang = a / 26 * TAU, rx = 9 - i * 2.5, ry = 3 - i * 0.8;
    put(Math.round(NB.x + 15 + Math.cos(ang) * rx), Math.round(NB.y + 11 + Math.sin(ang) * ry - i), a % 3 ? '#a08a62' : '#6a5a40');
  }
  return p.done();
}
function pipeAt(put, px0, py0) {                       // a pipe of port on its side, 19 x 10
  for (let y = 0; y < 10; y++) for (let x = 0; x < 19; x++) {
    const bulge = y === 0 || y === 9 ? 2 : (y === 1 || y === 8) ? 1 : 0;
    if (x < bulge || x > 18 - bulge) continue;
    const hoop = x === 1 || x === 4 || x === 14 || x === 17;
    const v = y / 9 + (bayer(x, y) - 0.5) * 0.2;
    put(px0 + x, py0 + y, hoop ? '#1e1418' : v < 0.25 ? '#c8905a' : v < 0.6 ? '#9a6638' : '#643e26');
  }
  for (let y = 2; y < 8; y++) put(px0 + 9, py0 + y, '#3a2418');
}
function renderPipes() {
  const p = painter(W, H);
  pipeAt(p.put, 437, 252); pipeAt(p.put, 457, 252); pipeAt(p.put, 447, 242);
  for (let x = 436; x < W; x++) p.put(x, 262, '#121218');
  return p.done();
}
function renderNearTable() {                            // an upright pipe as a table, two stools, a jar lantern
  const p = painter(W, H), put = p.put, { x: tx, base } = NEAR_TABLE;
  for (let y = base - 17; y <= base; y++) for (let x = tx - 7; x <= tx + 7; x++) {
    const bulge = (y > base - 14 && y < base - 3) ? 0 : 1;
    if (Math.abs(x - tx) > 7 - bulge) continue;
    const hoop = y === base - 15 || y === base - 11 || y === base - 5 || y === base - 1;
    let col = hoop ? '#24181a' : x < tx - 3 ? '#b07a4a' : x < tx + 3 ? '#8a5a34' : '#5e3a24';
    if (y === base - 17) col = '#d0a26a';
    if (!hoop && y > base - 17 && (x === tx - 3 || x === tx + 3)) col = '#4a2e20';
    put(x, y, col);
  }
  for (const sx of [tx - 19, tx + 13]) {
    for (let x = sx; x < sx + 7; x++) { put(x, base - 11, '#a8784a'); put(x, base - 10, '#5a3a24'); }
    for (const lx of [sx + 1, sx + 5]) for (let y = base - 9; y <= base; y++) put(lx, y, '#3a2a20');
  }
  for (let x = tx - 9; x <= tx + 21; x++) put(x, base + 1, '#121218');
  return p.done();
}


/* ================= The loop's timeline (seconds) =================
   t         what
   18-40     three gulls fly through the arch, left to right (again 138-160, right to left)
   26-50     the metro crosses the upper deck to Gaia (train at 26); the deck lamps glint ahead of it
   60-65     the Clérigos bell: three strokes (60, 62.5, 65); the gull on the bollard lifts off at 61, glides back 104-110
   70-130    a river cruise boat comes out from under the arch and goes downriver (horn at 72); its wake rocks the rabelos
   146-170   the metro crosses back to the Ribeira side (train at 146)
   180-185   the bell again; the gull lifts off at 181, back 224-230
   186-226   the toast at the lodge: the cellarman rolls a pipe out (186-196), the waiter fills the glasses (196-202),
             glasses raised and the busker plays to them (202-210, chime at 203), the pipe rolled back in (216-226)
   all loop  the waiter pours a glass every 12 s; windows switch on and off; cable-car cabins glide down and up Gaia's
             bluff; walkers on both decks, the Ribeira quay and the Gaia quay; drizzle at three depths            */
const METRO = [{ t0: 26, t1: 50, dir: 1 }, { t0: 146, t1: 170, dir: -1 }];
const BELLS = [60, 62.5, 65, 180, 182.5, 185];
const GULL_AWAY = [{ off: 61, back: 104, land: 110 }, { off: 181, back: 224, land: 230 }];
const FLOCK = [{ t0: 18, t1: 40, dir: 1 }, { t0: 138, t1: 160, dir: -1 }];
const CRUISE = { t0: 70, t1: 130, wl: 176 };
const TOAST = { out: 186, pour: 196, raise: 202, down: 210, back: 216, in: 226 };
const bellGlow = t => BELLS.reduce((a, b) => Math.max(a, t >= b && t < b + 2.4 ? 1 - (t - b) / 2.4 : 0), 0);

/* ----- the Ribeira's windows switching on and off (prerendered dark; lit per frame while on) ----- */
const WINS = [];
function drawWindows(t) {
  for (const w of WINS) {
    if (hash(w.id, Math.floor(frac(t / LOOP + w.ph) * w.n), 13) < 0.55) { ctx.fillStyle = w.on; ctx.fillRect(w.x, w.y, w.w, w.h); ctx.fillStyle = w.top; ctx.fillRect(w.x, w.y, w.w, 1); }
  }
}

/* ----- the upper deck's lamps, the metro, the walkers on both decks and on the Ribeira's quay ----- */
const DECK_LAMPS = [];
for (let x = BR.d0; x <= BR.d1; x++) if ((x - BR.d0) % 8 === 4) DECK_LAMPS.push(x);
const TRAM_ROWS = [
  '..............kk.........................', '.............k..k........................',
  '..ssssssssssssssssssssss.ssssssssssssssss..', '.sHHHHHHHHHHHHHHHHHHHHH.HHHHHHHHHHHHHHHHs.',
  'swwpwwwpwwwpwwwpwwwpwwws.wwwpwwwpwwwpwwwpys', 'swwpwwwpwwwpwwwpwwwpwwws.wwwpwwwpwwwpwwwpys',
  'sHHHHHHHHHHHHHHHHHHHHHHs.HHHHHHHHHHHHHHHHys', 'rddddddddddddddddddddddd.dddddddddddddddddf'];
const TRAM_W = Math.max(...TRAM_ROWS.map(r => r.length));
const TRAM_KEY = { k: '#5a5048', s: '#9aa0b0', H: '#d4d8e0', w: '#fff0c8', p: '#4a4e5c', y: '#e8c040', d: '#3a3e4c', r: '#ff4a3a', f: '#fff8e0' };
function metroAt(t) {
  const m = METRO.find(({ t0, t1 }) => t >= t0 && t < t1);
  if (!m) return null;
  const u = (t - m.t0) / (m.t1 - m.t0), L = TRAM_W, a = BR.d0 + 2 - L, b = BR.d1 - 2;
  return { x: Math.round(m.dir > 0 ? lerp(a, b, u) : lerp(b, a, u)), dir: m.dir, L };
}
function drawDeckLamps(t, m) {
  const s = step(t, 480);
  DECK_LAMPS.forEach((x, i) => {
    let a = 0.82 + 0.18 * wave(t, 40 + (i % 7) * 4, i * 0.13);
    if (m) { const ahead = (x - (m.dir > 0 ? m.x + m.L : m.x)) * m.dir; if (ahead > 0 && ahead < 36) a = 1; }
    const dim = hash(i, s, 23) < 0.03;
    ctx.fillStyle = dim ? '#b08a50' : a > 0.95 ? '#ffffff' : '#fff2c8';
    ctx.fillRect(x, BR.top - 2, 1, 1);
    halo(HL.deck, x, BR.top - 2, dim ? 0.3 : a * 0.9);
  });
}
function drawMetro(t, m) {
  if (!m) return;
  ctx.save(); ctx.beginPath(); ctx.rect(BR.d0 + 4, 0, BR.d1 - BR.d0 - 9, H); ctx.clip();
  ctx.drawImage(m.dir > 0 ? TRAM_C : TRAM_L, m.x, BR.top - TRAM_ROWS.length);
  ctx.restore();
  const hx = m.dir > 0 ? m.x + m.L + 1 : m.x - 1;
  if (hx > BR.d0 + 4 && hx < BR.d1 - 4) halo(HL.tram, hx, BR.top - 2, 1);
  halo(HL.tramWin, m.x + (m.L >> 1), BR.top - 4, 0.8);
}
// tiny walkers: [lane, crossings per loop, phase, direction, colour]
const TINY = [];
{
  const r = rng(91), C = ['#e0b8a0', '#c84a4a', '#4a7ac8', '#e8d8b8', '#7a5ac0', '#3a8a6a'];
  for (let i = 0; i < 4; i++) TINY.push({ lane: 'top', k: 1 + (i & 1), ph: r(), dir: i % 2 ? -1 : 1, col: C[i], rank: frac((i + 0.5) * 0.618) });
  for (let i = 0; i < 5; i++) TINY.push({ lane: 'low', k: 1, ph: r(), dir: i % 2 ? 1 : -1, col: C[(i + 2) % 6], rank: frac((i + 4.5) * 0.618) });
  for (let i = 0; i < 6; i++) TINY.push({ lane: 'quay', k: 1, ph: r(), dir: i % 2 ? -1 : 1, col: C[(i + 4) % 6], rank: frac((i + 9.5) * 0.618) });
}
const TINY_LANE = { top: [BR.d0 + 5, BR.d1 - 5, BR.top - 1, 3], low: [BR.l0 + 12, BR.l1 - 4, BR.ltop - 1, 5], quay: [-6, 186, 151, 6] };
function drawTiny(t, lane, crowd) {
  const [a, b, fy, h] = TINY_LANE[lane], st = step(t, 768);
  TINY.forEach((p, i) => {
    if (p.lane !== lane || p.rank >= crowd) return;
    const u = frac(p.k * t / LOOP + p.ph), x = Math.round(p.dir > 0 ? lerp(a, b, u) : lerp(b, a, u));
    ctx.fillStyle = p.col; ctx.fillRect(x, fy - h + 1, 1, h - 1);
    if (h > 3) { ctx.fillStyle = '#e8c0a0'; ctx.fillRect(x, fy - h + 1, 1, 1); }
    ctx.fillStyle = '#16141c';
    if ((st + i) & 1) ctx.fillRect(x, fy, 1, 1); else { ctx.fillRect(x - 1, fy, 1, 1); ctx.fillRect(x + 1, fy, 1, 1); }
  });
}

/* ----- the Gaia cable car: cabins gliding down to the quay and back up along the bluff ----- */
const CABLES = [{ x0: 444, y0: 73, x1: 486, y1: 90, k: 40, dir: 1 }, { x0: 444, y0: 76, x1: 486, y1: 93, k: 40, dir: -1 }];
function drawCableCar(t) {
  for (const c of CABLES) {
    ctx.fillStyle = '#3a3a48';
    for (let x = c.x0; x < W; x++) ctx.fillRect(x, Math.round(lerp(c.y0, c.y1, (x - c.x0) / (c.x1 - c.x0))), 1, 1);
    const off = frac(c.k * t / LOOP) * 18;
    for (let k = -1; k < 3; k++) {
      const x = Math.round(c.x0 + (c.dir > 0 ? off : 18 - off) + k * 18);
      if (x < c.x0 + 2 || x > W) continue;
      const y = Math.round(lerp(c.y0, c.y1, (x - c.x0) / (c.x1 - c.x0)));
      ctx.drawImage(CABIN_C, x - 2, y + 1);
    }
  }
}

/* ----- the cruise boat under the arch and its wake ----- */
const BOAT_ROWS = [
  '......................................w.....', '................................kkkkkkk.....',
  '..........hhhhhhhhhhhhhhhhhhhh...kYYYYYk.....', '.......rrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrr..',
  '......cccccccccccccccccccccccccccccccccccccc..', '.....cWWWpWWWpWWWpWWWpWWWpWWWpWWWpWWWpWWWc..',
  '.....cWWWpWWWpWWWpWWWpWWWpWWWpWWWpWWWpWWWc..', '.....cccccccccccccccccccccccccccccccccccccc..',
  '.g.eeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', '..nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn.',
  '...nnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnnn..', '....RRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRR...'];
const BOAT_KEY = { w: '#ffffff', k: '#2a2e3a', Y: '#ffe2a0', h: '#3a2a26', r: '#c8c4b8', c: '#e0dcd0', W: '#ffd890', p: '#5a5048',
  g: '#4aff8a', e: '#f0ece0', n: '#1c2846', R: '#9a2a34' };
function boatAt(t) {
  if (t < CRUISE.t0 || t >= CRUISE.t1) return null;
  return { x: Math.round(lerp(420, -50, (t - CRUISE.t0) / (CRUISE.t1 - CRUISE.t0))) };
}
// wake: how strongly the boat's wake rocks something at column x (it reaches the rabelos a few seconds after it passes)
function wakeAt(t, x) {
  const at = CRUISE.t0 + (420 - x) / 470 * (CRUISE.t1 - CRUISE.t0) + 4;
  return t > at && t < at + 16 ? Math.sin(Math.PI * (t - at) / 16) : 0;
}
function drawBoat(t, b) {
  if (!b) return;
  const wl = CRUISE.wl, h = BOAT_ROWS.length;
  ctx.save(); ctx.beginPath(); ctx.rect(0, 0, LODGE.x0, H); ctx.clip();
  // the wake fanning out behind it, foam flickering
  for (let k = 0; k < 34; k++) {
    const x = b.x + 44 + k, spread = Math.round(k * 0.12), f = hash(k, step(t, 960), 31);
    ctx.globalAlpha = (1 - k / 34) * 0.55;
    if (f < 0.75) { ctx.fillStyle = '#c8d8d8'; ctx.fillRect(x, wl - 1 - spread, 1, 1); }
    if (f > 0.2) { ctx.fillStyle = '#a8bcc0'; ctx.fillRect(x, wl + 1 + spread, 1, 1); }
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(BOAT_C, b.x, wl - h + 1);
  ctx.restore();
  halo(HL.boat, b.x + 24, wl - 6, 0.9);
  if (b.x + 2 < LODGE.x0) halo(HL.green, b.x + 1, wl - h + 9, 0.8);
  streak(ctx, t, b.x + 24, 22, '#ffc870', 0.3, 5, wl + 1, KERB);
}

/* ----- gulls: three through the arch, and the one on the bollard that lifts off at the bell ----- */
function gullFly(x, y, flap, col = '#e8eaf0') {
  x = Math.round(x); y = Math.round(y);
  ctx.fillStyle = col; ctx.fillRect(x - 1, y, 3, 1);
  if (flap) { ctx.fillRect(x - 2, y - 1, 1, 1); ctx.fillRect(x + 2, y - 1, 1, 1); ctx.fillRect(x - 3, y - 2, 1, 1); ctx.fillRect(x + 3, y - 2, 1, 1); }
  else { ctx.fillRect(x - 2, y, 1, 1); ctx.fillRect(x + 2, y, 1, 1); ctx.fillRect(x - 3, y + 1, 1, 1); ctx.fillRect(x + 3, y + 1, 1, 1); }
  ctx.fillStyle = '#8a90a0'; ctx.fillRect(x, y, 1, 1);
}
function drawFlock(t) {
  const f = FLOCK.find(({ t0, t1 }) => t >= t0 && t < t1);
  if (!f) return;
  const u = (t - f.t0) / (f.t1 - f.t0), base = f.dir > 0 ? lerp(-12, 492, u) : lerp(492, -12, u), fl = step(t, 1200);
  [[0, 0, 0], [-9, 5, 0.3], [-17, -3, 0.6]].forEach(([dx, dy, ph], i) =>
    gullFly(base + dx * f.dir, 112 + dy + 6 * Math.sin(TAU * (u * 2 + ph)), ((fl + i) >> 1) & 1));
}
function drawBollardGull(t) {
  const bx = BOLLARDS[1], perched = { x: bx, y: KERB - 7 };
  const g = GULL_AWAY.find(({ off, land }) => t >= off && t < land);
  if (!g) { ctx.drawImage(GULL_C, perched.x - 3, perched.y - 4); return; }
  const fl = (step(t, 1200) >> 1) & 1;
  if (t < g.off + 6) { const u = (t - g.off) / 6; gullFly(lerp(perched.x, perched.x - 70, u), lerp(perched.y - 2, 120, u * (2 - u)), fl); return; }
  if (t >= g.back) { const u = (t - g.back) / (g.land - g.back); gullFly(lerp(perched.x - 90, perched.x, u), lerp(110, perched.y - 2, u * u), u > 0.8 ? 0 : fl); }
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#c8a058', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffd8a0';
// a look for a person h px tall: proportions and colours dimmed for the night, with a warm line of light
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * 0.37);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.32), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, apron: o.apron ? m(o.apron) : null, scarf: o.scarf ? m(o.scarf) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (pouring, offering), 4 glass raised, 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (o.sit && view === 1) {
    R(tx, fy + 1, 2, 4, legC); R(tx + bw - 2, fy + 1, 2, 4, legC); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit && side) {
    const tl = P.h >= 22 ? 7 : 5, sh = o.shin ?? 4;
    R(dir > 0 ? x - 1 : x - tl + 2, fy - 1, tl, 2, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 2, fy + 1, 2, sh, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 1, fy + 1 + sh, 3, 1, P.shoe);
  }
  else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                  // the lamps' light on the shoulders
  if (P.apron && view !== 0) R(side ? (dir > 0 ? x : x - 1) : tx + 1, ty + 2, side ? 2 : bw - 2, th + 2, P.apron);
  if (P.scarf) R(tx, ty, bw, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ad8e8'); } }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(ax, ty - 1, 1, 3, P.topD); R(ax, ty - 2, 1, 1, P.skin); R(ax, ty - 5, 1, 3, '#9a2438'); R(ax, ty - 5, 1, 1, '#f0c8c8'); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(x + dir, ty - 1, 1, 3, P.topD); R(x + dir, ty - 2, 1, 1, P.skin); R(x + dir * 2, ty - 4, 1, 3, '#9a2438'); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
const TOPS = ['#c8404a', '#e0b840', '#e8e2d4', '#3a64c0', '#2a9a8a', '#d87aa0', '#e0803a', '#4a8a4a', '#7a5ac0', '#c0c8d8', '#8a2a34', '#5aa8d0'];
const BOTS = ['#33466e', '#22202c', '#a8926a', '#d0ccc4', '#3a3a58', '#4a3a30'];

/* ================= The port lodge on the right: the cozy corner ================= */
// a whitewashed lodge on the Gaia quay: a tiled roof carrying its sign, granite quoins and plinth, an azulejo panel,
// a wall lantern, the big arched door open on the pipes of port inside, and its terrace of barrel tables on the quay
const DOOR = { x0: 444, x1: 464, top: 164 };
const TABLES = [390, 430];
const STRING = { x0: 376, x1: 417, y: 155 };                     // festoon bulbs from the lodge wall to a pole
function renderLodge() {
  const p = painter(W, H), put = p.put, { x0, eave } = LODGE, floor = LODGE.floor - 4;   // the wall meets the deck
  // the roof, seen from the quay: rows of terracotta tiles back to the ridge
  for (let y = eave - 9; y < eave; y++) for (let x = x0 - 3 + Math.max(0, (eave - y) - 6); x < W; x++) {
    const row = eave - y, cap = (x + (row & 1) * 2) % 4;
    put(x, y, row === 9 ? '#4a2218' : cap === 0 ? '#6a2e1e' : row % 2 ? '#b0502e' : '#943f25');
  }
  for (let x = x0 - 3; x < W; x++) { put(x, eave, '#d8c8a8'); put(x, eave + 1, '#6a5a50'); put(x, eave + 2, '#4a3e3c'); }
  // the wall, lamplit warmest round the door and the lantern
  for (let y = eave + 3; y < floor - 7; y++) for (let x = x0; x < W; x++) {
    const glow = Math.max(0, 1 - Math.hypot((x - 446) / 40, (y - 178) / 30)) + Math.max(0, 1 - Math.hypot((x - 430) / 22, (y - 140) / 14)) * 0.6;
    const v = 0.3 + glow * 0.7 + (bayer(x, y) - 0.5) * 0.22;
    p.put(x, y, v > 0.78 ? '#f4e4c2' : v > 0.52 ? '#e0caa0' : v > 0.34 ? '#c0a882' : '#8e7c66');
  }
  for (let y = eave + 3; y < floor; y++) {                              // granite quoins on the corner
    const long = (Math.floor((y - eave - 3) / 5) & 1) === 0;
    for (let x = x0; x < x0 + (long ? 5 : 3); x++) put(x, y, (y - eave - 3) % 5 === 4 ? '#5a4c46' : x === x0 ? '#d8c4a2' : '#a89074');
  }
  for (let y = floor - 7; y < floor; y++) for (let x = x0; x < W; x++)    // the granite plinth
    put(x, y, y === floor - 7 ? '#c8b494' : (x + (y > floor - 4 ? 4 : 0)) % 9 === 0 ? '#3e3438' : y > floor - 3 ? '#5e5050' : '#7a6a60');
  // the azulejo panel: blue on white tiles, a border and a little boat under sail in the middle
  const AZ = { x: x0 + 7, y: eave + 33, w: 19, h: 24 };
  for (let y = AZ.y; y < AZ.y + AZ.h; y++) for (let x = AZ.x; x < AZ.x + AZ.w; x++) {
    const bx = x - AZ.x, by = y - AZ.y, border = bx < 2 || by < 2 || bx > AZ.w - 3 || by > AZ.h - 3;
    let col = border ? ((bx + by) & 1 ? '#2a4a9a' : '#e6ecf4') : ((bx % 4 === 0 || by % 4 === 0) ? '#c8d4e8' : '#eef2f8');
    if (!border && (bx + by * 3) % 7 === 0) col = '#6a8ac8';
    p.put(x, y, col);
  }
  const boat = ['....b....', '...bbb...', '..bbbbb..', '....b....', 'bbbbbbbbb', '.bbbbbbb.', '..wwwww..'];
  boat.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === 'b') p.put(AZ.x + 5 + i, AZ.y + 7 + j, '#1f4fa0'); });
  for (let x = AZ.x + 3; x < AZ.x + AZ.w - 3; x++) p.put(x, AZ.y + 15 + (x & 1), '#3a62b0');
  // the arched door: barrel vault inside lit amber, pipes of port on their sides in racks, the floor
  const { x0: dx0, x1: dx1, top: dtop } = DOOR, dcx = (dx0 + dx1) / 2, hw = (dx1 - dx0) / 2;
  const inDoor = (x, y) => y >= dtop + 6 ? x >= dx0 && x <= dx1 : Math.hypot((x - dcx) / hw, (y - (dtop + 6)) / 6.5) <= 1;
  for (let y = dtop - 2; y < floor; y++) for (let x = dx0 - 2; x <= dx1 + 2; x++) {
    if (inDoor(x, y)) {
      const v = (y - dtop) / (floor - dtop) + (bayer(x, y) - 0.5) * 0.2;
      put(x, y, v < 0.3 ? '#8a4e26' : v < 0.55 ? '#c47a38' : v < 0.8 ? '#e8a456' : '#f6c47a');
    } else if (inDoor(x + 2, y) || inDoor(x - 2, y) || inDoor(x, y + 2)) put(x, y, x < dcx ? '#e8d6b0' : '#a89076');   // granite surround
  }
  for (const [ry, n] of [[179, 3], [186, 3], [193, 3]]) for (let k = 0; k < n; k++) {
    const bx = dx0 + 3 + k * 6, by = ry;
    for (let j = -2; j <= 2; j++) for (let i = -2; i <= 2; i++) {
      const d = Math.hypot(i, j);
      if (d <= 2.5) put(bx + i, by + j, d > 1.6 ? '#3a1e14' : d > 0.8 ? '#7a4422' : '#a8602e');
    }
  }
  for (let x = dx0; x <= dx1; x++) { put(x, floor - 5, '#5a3018'); put(x, floor - 4, '#d89a54'); }
  // the window to the right: iron bars over warm glass
  for (let y = 170; y < 190; y++) for (let x = 468; x < 478; x++) {
    const frame = x === 468 || x === 477 || y === 170 || y === 189;
    put(x, y, frame ? '#d8c4a2' : (x - 469) % 3 === 2 ? '#2a1e1a' : y < 176 ? '#ffd890' : '#eaa458');
  }
  // the upper storey: a granite string course, tall windows onto iron balconies, one lit with its curtains open, one
  // behind green shutters, geraniums on the rail
  for (let x = x0; x < W; x++) { put(x, eave + 26, '#d8c8a8'); put(x, eave + 27, '#7a6a5e'); }
  for (const [wx, kind] of [[424, 'lit'], [446, 'shut'], [467, 'dim']]) {
    for (let y = eave + 6; y < eave + 22; y++) for (let x = wx; x < wx + 9; x++) {
      const frame = x === wx || x === wx + 8 || y === eave + 6;
      let col = frame ? '#d8c4a2' : kind === 'lit' ? (y < eave + 11 ? '#ffe2a0' : '#f4b860') : kind === 'dim' ? (y < eave + 12 ? '#c88a4a' : '#9a6234') : '#2a4a3a';
      if (!frame && kind === 'shut' && (y - eave) % 2 === 0) col = '#1e3a2c';
      if (!frame && kind !== 'shut' && x === wx + 4) col = '#5a3a24';
      if (!frame && kind === 'lit' && (x === wx + 1 || x === wx + 7)) col = '#b4523a';
      put(x, y, col);
    }
    for (let x = wx - 2; x <= wx + 10; x++) { put(x, eave + 22, '#c8b494'); put(x, eave + 18, '#1a1418'); if ((x - wx) % 2 === 0) for (let y = eave + 19; y < eave + 22; y++) put(x, y, '#1a1418'); }
    if (kind !== 'dim') for (let k = 0; k < 7; k++) put(wx - 1 + Math.floor(hash(k, wx, 1) * 11), eave + 17 - Math.floor(hash(k, wx, 2) * 2), hash(k, wx, 3) < 0.5 ? '#d83a3a' : '#3a7a3a');
  }
  // the PROVAS board over the door and the wall lantern's bracket
  for (let y = 154; y < 162; y++) for (let x = 441; x < 468; x++) put(x, y, y === 154 || y === 161 ? '#3a2418' : '#5e3a22');
  for (let x = 437; x < 441; x++) put(x, 163, '#1a1418');
  put(437, 164, '#1a1418');
  // the pole of the festoon string, and the barrels of the terrace standing on the quay
  for (let y = STRING.y - 2; y < floor + 1; y++) { put(STRING.x0, y, '#2a2a34'); put(STRING.x0 + 1, y, '#16161e'); }
  const c = p.done();
  sign(p.x, 'PROVAS', 444, 156, '#f2d8a0');
  return c;
}
function renderBarrel() {                                            // an upright pipe of port used as a table, 11 x 13
  const p = painter(11, 14);
  for (let y = 0; y < 14; y++) for (let x = 0; x < 11; x++) {
    const bulge = (y > 2 && y < 11) ? 0 : 1;
    if (x < bulge || x > 10 - bulge) continue;
    const hoop = y === 1 || y === 4 || y === 9 || y === 12;
    let col = hoop ? '#24181a' : x < 3 ? '#b07a4a' : x < 7 ? '#8a5a34' : '#5e3a24';
    if (y === 0) col = '#c89a64';
    if (!hoop && y > 0 && (x === 3 || x === 7)) col = '#4a2e20';
    p.put(x, y, col);
  }
  return p.done();
}
const GULL = ['....wwy', '.gggww.', 'ggggww.', '..www..', '..k.k..'];
const GULL_KEY = { w: '#e6e8ee', g: '#8a90a0', y: '#e8b840', k: '#d88a3a' };

/* ================= The lodge's roof sign: the billboard ================= */
// a steel frame on the roof ridge with a catwalk and three small lamps; the city's own ad is the lodge's name: CAVES
// LUAR (moonlight cellars, a made-up house), a crescent moon and a glass of ruby port
const BB_LAMPS = [0.2, 0.5, 0.8].map(f => BB.x + Math.round((BB.w - 1) * f));
function renderBillboard() {
  const p = painter(W, H), put = p.put, X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const cx of [BB.x + 8, BB.x + BB.w - 9]) for (let y = Y1 + 1; y < LODGE.eave - 6; y++) { put(cx, y, '#3a3a48'); put(cx + 1, y, '#16161e'); }
  for (let x = X0 - 3; x <= X1 + 3; x++) { put(x, Y1 + 1, '#4a4a5a'); put(x, Y1 + 2, (x & 1) ? '#22222c' : '#16161e'); }
  for (let x = X0; x <= X1; x++) { put(x, Y0, '#14141c'); put(x, Y1, '#14141c'); put(x, Y0 + 1, '#2e3040'); put(x, Y1 - 1, '#6a5040'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#14141c'); put(X1, y, '#14141c'); put(X0 + 1, y, '#2a2c3c'); put(X1 - 1, y, '#16161e'); }
  for (const lx of BB_LAMPS) {
    for (let y = Y0 - 4; y < Y0; y++) put(lx, y, '#3a3a48');
    for (let i = lx - 2; i <= lx + 2; i++) put(i, Y0 - 5, i === lx - 2 ? '#4a4a5a' : '#2a2a36');
  }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + x - lx))) / 9;
    const v = clamp(y / h * 0.8 + lamp * 0.25, 0, 1);
    put(x, y, ramp(['#6e2234', '#5a1a2a', '#461422', '#360e1a'], v, x, y));
  }
  for (let x = 1; x < w - 1; x++) { put(x, 1, '#c8984a'); put(x, h - 2, '#a87a3a'); }
  for (let y = 1; y < h - 1; y++) { put(1, y, '#c8984a'); put(w - 2, y, '#a87a3a'); }
  // the crescent moon
  for (let y = -5; y <= 5; y++) for (let x = -5; x <= 5; x++) if (Math.hypot(x, y) <= 4.6 && Math.hypot(x - 2.2, y - 1) > 3.6) put(7 + x, 10 + y, x < -2 ? '#fff0c0' : '#f2d088');
  // the glass of ruby port on its stem
  const G = ['wrrrw', 'wrrrw', '.rrr.', '..w..', '..w..', '.www.'];
  G.forEach((row, j) => { for (let i = 0; i < 5; i++) if (row[i] !== '.') put(43 + i, 7 + j, row[i] === 'r' ? (i === 1 ? '#e8506a' : '#b8243c') : '#e8dcc8'); });
  const c = p.done();
  sign(p.x, 'CAVES', 16, 3, '#f2e2c0');
  const [g, gx] = makeCanvas(w, h);                                  // LUAR at twice the size, in gold
  sign(gx, 'LUAR', 0, 0, '#ffd27a');
  p.x.imageSmoothingEnabled = false;
  p.x.drawImage(g, 0, 0, 15, 5, 12, 9, 30, 10);
  return gradeAd(c, { level: 0.86, tint: '#141a3a', mix: 0.08, wear: 0.18, seed: 41 });
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 7, 3, 1, '#f2dcaa'); halo(HL.bbLamp, lx, BB.y - 6, 0.9); }
}

/* ================= The cast: people who stay, each with a verb on the loop =================
   rank: drawn while rank < crowd (Settings, People); rank 0 is there at every level above 0 (the busker, the waiter,
   the cellarman and the couple of the toast, the kiosk's seller); couples share one rank */
const rk = i => frac((i + 0.5) * 0.618);
const CAST = [
  { id: 'busker', x: 92, y: FEET - 5, P: look(19, 1, 0, TOPS[10], BOTS[1], { hat: '#2a2a34' }), rank: 0 },
  { id: 'film', x: 268, y: FEET - 1, P: look(19, 1, 3, TOPS[3], BOTS[2], { long: 2 }), rank: rk(8) },
  { id: 'kerbA', x: 314, y: KERB + 1, P: look(18, 0, 1, TOPS[1], BOTS[0]), rank: rk(2) },
  { id: 'kerbB', x: 323, y: KERB + 1, P: look(18, 3, 0, TOPS[11], BOTS[4], { long: 3 }), rank: rk(2) },
  { id: 'sip', x: 380, y: TERRACE - 6, P: look(19, 0, 3, TOPS[5], BOTS[1], { long: 3 }), rank: rk(6) },
  { id: 'waiter', x: 401, y: TERRACE, P: look(21, 2, 0, '#e8e4dc', '#1a1a22', { apron: '#2a2026' }), rank: 0 },
  { id: 'toastA', x: 422, y: TERRACE, P: look(21, 1, 1, TOPS[7], BOTS[0]), rank: 0 },
  { id: 'toastB', x: 439, y: TERRACE, P: look(20, 0, 4, TOPS[0], BOTS[3], { long: 4 }), rank: 0 },
  { id: 'cellar', x: 455, y: LODGE.floor - 8, P: look(18, 2, 0, '#5a3a2a', '#2a2026', { apron: '#3a2a22', dk: 0.5 }), rank: 0 },
  { id: 'benchA', x: 226, y: BENCH.y, P: look(24, 0, 1, '#4a5a7a', BOTS[1], { dk: 0.34 }), rank: rk(0), base: BENCH.y + 6 },
  { id: 'benchB', x: 237, y: BENCH.y, P: look(23, 0, 4, TOPS[5], BOTS[0], { long: 4, dk: 0.34 }), rank: rk(0), base: BENCH.y + 6 },
  { id: 'tableA', x: NEAR_TABLE.x - 16, y: NEAR_TABLE.base - 11, P: look(25, 2, 2, '#3a6a8a', BOTS[5], { dk: 0.26 }), rank: rk(3), base: NEAR_TABLE.base + 1 },
  { id: 'tableB', x: NEAR_TABLE.x + 16, y: NEAR_TABLE.base - 11, P: look(24, 0, 3, '#c8584a', BOTS[0], { long: 4, dk: 0.26 }), rank: rk(3), base: NEAR_TABLE.base + 1 },
];
const SELLER = look(19, 0, 5, '#2a5a8a', BOTS[1], { dk: 0.1 });
// the toast's pipe: rolled out of the lodge door onto the deck and back in
function pipeOut(t) {
  if (t < TOAST.out || t >= TOAST.in) return null;
  if (t < TOAST.pour) return { x: lerp(448, 464, (t - TOAST.out) / (TOAST.pour - TOAST.out)), roll: true };
  if (t >= TOAST.back) return { x: lerp(464, 448, (t - TOAST.back) / (TOAST.in - TOAST.back)), roll: true };
  return { x: 464, roll: false };
}
function castPose(c, t) {
  const o = {}, toast = t >= TOAST.raise && t < TOAST.down, m = metroAt(t), boat = boatAt(t);
  switch (c.id) {
    case 'busker': return { view: toast ? 2 : 1, dir: 1, sit: true };
    case 'film': o.aR = m || (boat && boat.x > 150 && boat.x < 380) || frac(t / 30) < 0.4 ? 2 : 0; return { view: 0, ...o };
    case 'kerbA': return { view: 0, sit: true, lean: frac(t / 40) < 0.4 ? 1 : 0 };
    case 'kerbB': return { view: 0, sit: true, lean: frac(t / 40 + 0.1) < 0.45 ? -1 : 0, aL: boat && boat.x > 200 && boat.x < 360 ? 3 : 0 };
    case 'sip': return { view: 2, dir: 1, sit: true, aR: toast || frac(t / 16 + 0.3) < 0.3 ? 4 : 3 };
    case 'waiter': {
      const pouring = t >= TOAST.pour && t < TOAST.raise, at2 = t >= TOAST.pour - 2 && t < TOAST.back;
      return { view: 2, dir: at2 ? 1 : -1, aR: pouring || (!at2 && frac(t / 12) < 0.25) ? 3 : 0, x: at2 ? 412 : c.x, step: 0 };
    }
    case 'toastA': return { view: 2, dir: 1, aR: toast || frac(t / 20) < 0.22 ? 4 : 0 };
    case 'toastB': return { view: 2, dir: -1, aR: toast || frac(t / 20 + 0.05) < 0.22 ? 4 : 0 };
    case 'cellar': {
      const p = pipeOut(t);
      if (!p) return { view: 1 };
      return { view: p.roll ? 2 : 1, dir: t < TOAST.pour ? 1 : -1, x: Math.round(p.x - 4), y: TERRACE, step: p.roll ? step(t, 768) & 1 : 0 };
    }
    case 'benchA': return { view: 0, sit: true, aR: m ? 3 : 0 };
    case 'benchB': return { view: 0, sit: true, lean: -1 };
    case 'tableA': return { view: 2, dir: 1, sit: true, shin: 9, aR: toast || frac(t / 24) < 0.25 ? 4 : 3 };
    case 'tableB': return { view: 2, dir: -1, sit: true, shin: 9, aR: frac(t / 24 + 0.5) < 0.25 ? 4 : 0 };
  }
  return o;
}
function drawCastMember(c, t) {
  const o = castPose(c, t), x = o.x ?? c.x, y = o.y ?? c.y;
  if (c.id === 'busker') {
    R(c.x - 5, c.y + 1, 11, 5, '#3e3028'); R(c.x - 5, c.y + 1, 11, 1, '#6a5444'); R(c.x - 5, c.y + 3, 11, 1, '#2a201c');
    const hy = person(x, y, c.P, o), fy = c.y, strum = (step(t, t >= TOAST.raise && t < TOAST.back ? 1920 : 960) & 1);
    R(c.x - 5, fy - 4, 5, 4, '#c88a4a'); R(c.x - 4, fy - 4, 3, 1, '#e0a862'); R(c.x - 3, fy - 3, 2, 2, '#2a1a14');
    for (let k = 0; k < 7; k++) R(c.x + k, fy - 4 - (k >> 1), 1, 1, '#6a4a2a');
    R(c.x + 7, fy - 8, 2, 1, '#3a2a20'); R(c.x + 5, fy - 7, 1, 2, c.P.skin); R(c.x - 2 + strum, fy - 2 - strum, 1, 1, c.P.skin);
    R(c.x + 9, FEET - 2, 11, 3, '#1a1418'); R(c.x + 10, FEET - 2, 9, 1, '#8a2a30');
    R(c.x + 12, FEET - 1, 1, 1, '#e8c860'); R(c.x + 15, FEET - 1, 1, 1, '#d8d8e0'); R(c.x + 17, FEET - 1, 1, 1, '#e8c860');
    return hy;
  }
  if (c.id === 'cellar') {
    const p = pipeOut(t);
    if (p) {                                                       // the pipe on its side, its hoops turning as it rolls
      const px = Math.round(p.x), ph = p.roll ? step(t, 960) & 1 : 0;
      for (let yy = 0; yy < 8; yy++) for (let xx = 0; xx < 14; xx++) {
        const b = yy === 0 || yy === 7 ? 1 : 0;
        if (xx < b || xx > 13 - b) continue;
        R(px + xx, TERRACE - 7 + yy, 1, 1, (xx === 2 + ph || xx === 11 - ph) ? '#1e1418' : yy < 3 ? '#c8905a' : yy < 6 ? '#9a6638' : '#643e26');
      }
    }
    if (!p) { ctx.save(); ctx.beginPath(); ctx.rect(DOOR.x0, DOOR.top, DOOR.x1 - DOOR.x0 + 1, LODGE.floor - DOOR.top); ctx.clip(); }
    const hy = person(x, y, c.P, o);
    if (!p) ctx.restore();
    return hy;
  }
  const hy = person(x, y, c.P, o);
  if (c.id === 'waiter' && o.aR === 3) {                            // the bottle tipped over a glass, a thread of ruby
    const d = o.dir;
    R(x + d * 5, hy + 6, 1, 3, '#2a0a14'); R(x + d * 5, hy + 5, 1, 1, '#c8b890'); R(x + d * 6, hy + 9, 1, 2, '#c02a40');
  }
  return hy;
}
function drawSeller(t) {
  const { x0, x1, top } = KIOSK;
  ctx.save(); ctx.beginPath(); ctx.rect(x0 + 5, top + 18, x1 - x0 - 11, 14); ctx.clip();
  const turn = frac(t / 8) < 0.3 ? 2 : frac(t / 8) < 0.6 ? 1 : 0;
  person(82 + (turn === 2 ? 1 : 0), top + 39, SELLER, { view: turn === 1 ? 1 : 2, dir: turn === 2 ? 1 : -1, aR: frac(t / 24) < 0.2 ? 3 : 0 });
  ctx.restore();
}

/* ================= Walkers: along the kerb and down past the terrace, and across the near setts ================= */
// lane A follows the kerb, then turns into the street towards the viewer (growing as it comes, out at the bottom); lane B crosses
// the near setts. Each crosses a whole number of times a loop, in either direction.
const LANE_A = [[-12, FEET], [330, FEET], [338, 228], [346, 296]], LANE_B = [[-12, 257], [492, 257]];
const laneLen = L => L.slice(1).map((p, i) => Math.hypot(p[0] - L[i][0], p[1] - L[i][1]));
const LEN_A = laneLen(LANE_A), LEN_B = laneLen(LANE_B);
function onLane(L, lens, u) {
  let d = u * lens.reduce((a, b) => a + b, 0);
  for (let i = 0; i < lens.length; i++) {
    if (d <= lens[i] || i === lens.length - 1) { const f = clamp(d / lens[i], 0, 1), ddx = L[i + 1][0] - L[i][0], ddy = L[i + 1][1] - L[i][1]; return { x: lerp(L[i][0], L[i + 1][0], f), y: lerp(L[i][1], L[i + 1][1], f), dx: Math.sign(ddx), down: Math.abs(ddy) > Math.abs(ddx) ? Math.sign(ddy) : 0 }; }
    d -= lens[i];
  }
}
const WALKERS = [
  { lane: 'A', k: 1, ph: 0.06, dir: 1, P: [1, 2, TOPS[8], BOTS[0], { long: 3 }], umbrella: '#c84a5a', mate: [0, 0, TOPS[9], BOTS[1], {}] },
  { lane: 'A', k: 2, ph: 0.38, dir: -1, P: [2, 0, '#e8e8f0', '#22222c', {}], jog: true },
  { lane: 'A', k: 1, ph: 0.58, dir: 1, P: [0, 5, '#5a6a4a', BOTS[2], { hat: '#3a3a44' }], dog: '#c89a5a' },
  { lane: 'A', k: 1, ph: 0.83, dir: -1, P: [3, 1, TOPS[6], BOTS[4], { long: 3, skirt: '#2a2a40' }] },
  { lane: 'B', k: 1, ph: 0.27, dir: -1, P: [0, 3, TOPS[2], BOTS[0], { long: 3 }], mate: [1, 0, '#4a4a5a', BOTS[1], {}] },
  { lane: 'B', k: 1, ph: 0.66, dir: 1, P: [2, 1, '#2a4a7a', BOTS[2], {}], umbrella: '#2a2a34' },
].map((w, i) => ({ ...w, rank: rk([5, 10, 11, 1, 7, 4][i]) }));
const lookCache = new Map();
function walkerLook(w, which, h) {
  const key = `${WALKERS.indexOf(w)}${which}${h}`;
  if (!lookCache.has(key)) { const [s, hr, tp, bt, o] = which ? w.mate : w.P; lookCache.set(key, look(h, s, hr, tp, bt, { ...o, dk: 0.2 + (h - 19) * 0.025 })); }
  return lookCache.get(key);
}
function walkerAt(w, t) {
  const u0 = frac(w.k * t / LOOP + w.ph), u = w.dir > 0 ? u0 : 1 - u0;
  const p = w.lane === 'A' ? onLane(LANE_A, LEN_A, u) : onLane(LANE_B, LEN_B, u);
  return { x: p.x, y: Math.round(p.y), face: p.dx * w.dir || 1, view: p.down ? (p.down * w.dir > 0 ? 1 : 0) : 2 };
}
function drawWalker(w, t) {
  const { x, y, face, view } = walkerAt(w, t), h = Math.round(clamp(19 + (y - FEET) * 0.12, 19, 26)), side = view === 2;
  const st = (step(t, w.jog ? 1440 : 768) + Math.round(w.ph * 10)) & 1;
  if (w.dog) {                                                     // a small dog trotting ahead on its lead
    const dx = Math.round(x + (side ? face * 9 : 8)), dy = y;
    R(dx - 3, dy - 4, 6, 2, w.dog); R(dx + (face > 0 ? 2 : -4), dy - 6, 3, 3, w.dog); R(dx + (face > 0 ? 4 : -5), dy - 5, 1, 1, '#1a1418');
    R(dx - 3, dy - 2, 1, 2, w.dog); R(dx + 2, dy - 2, 1, 2, w.dog); if (st) { R(dx - 2, dy - 1, 1, 1, w.dog); R(dx + 1, dy - 1, 1, 1, w.dog); }
    R(face > 0 ? dx - 4 : dx + 3, dy - 5, 1, 1, w.dog);
    for (let k = 1; k < 7; k++) R(Math.round(x + (side ? face : 1) * (2 + k)), Math.round(y - h * 0.5 + k * (h * 0.5 - 5) / 7), 1, 1, '#3a2a26');
  }
  const P = walkerLook(w, 0, h);
  let hy = person(x, y, P, { view, dir: face, step: side ? st : 0 });
  if (w.mate) { const Q = walkerLook(w, 1, h + 1); hy = Math.min(hy, person(x - (side ? face : 1) * 7, y, Q, { view, dir: face, step: side ? st ^ 1 : 0 })); }
  if (w.umbrella) drawUmbrella(Math.round(x - (w.mate ? face * 3 : 0)), hy - 7, w.umbrella, h);
}
function drawUmbrella(x, y, col, h = 19) {
  const r = h >= 23 ? 9 : 7;
  for (let k = 0; k < 4; k++) R(x - r + k + (k ? 1 : 0), y + k, 2 * r + 1 - 2 * k - (k ? 2 : 0), 1, k === 0 ? mixHex(col, '#000000', 0.35) : col);
  R(x - r + 1, y + 3, 2 * r - 1, 1, mixHex(col, '#000000', 0.45));
  R(x, y + 4, 1, 6, '#1a1a22');
  R(x - 2, y, 4, 1, mixHex(col, '#ffffff', 0.25));
}
// pigeons pecking on the near setts; they flutter up when a walker comes close
function drawPigeons(t) {
  const near = WALKERS.filter(w => w.rank < curCrowd).map(w => walkerAt(w, t)).filter(p => p.y > 240);
  PIGEONS.forEach(([px0, ph], i) => {
    const away = near.some(p => Math.abs(p.x - px0) < 14), peck = frac(t / 3 + ph) < 0.3, x = px0, y = 262 - (away ? 3 + (step(t, 1200) & 1) : 0);
    R(x - 2, y - 3, 5, 2, '#6a6e80'); R(x - 1, y - 4, 3, 1, '#8a8ea0'); R(x - 3, y - 2, 1, 1, '#4a4e5c');
    if (away) { R(x - 4, y - 5, 2, 1, '#9a9eb0'); R(x + 3, y - 5, 2, 1, '#9a9eb0'); }
    const hx = x + 3, hy = peck && !away ? y - 2 : y - 5;
    R(hx, hy, 2, 2, '#5a6278'); R(hx, hy + 1, 1, 1, '#4a8a7a'); R(hx + 2, hy + 1, 1, 1, '#d8a050');
    if (!away) { R(x - 1, y - 1, 1, 1, '#c86a5a'); R(x + 1, y - 1, 1, 1, '#c86a5a'); }
  });
}
let curCrowd = 1;
// everything near the viewer in feet order: the cast, the walkers, the kiosk, the lamp, the bench, the table, the pipes
function drawNear(t, crowd) {
  curCrowd = crowd;
  const DL = [];
  for (const c of CAST) if (c.rank < crowd) DL.push([c.base ?? (castPose(c, t).y ?? c.y), () => drawCastMember(c, t)]);
  for (const w of WALKERS) if (w.rank < crowd) DL.push([walkerAt(w, t).y, () => drawWalker(w, t)]);
  DL.push([KIOSK.base, () => { ctx.drawImage(KIOSK_C, 0, 0); if (crowd > 0) drawSeller(t); }]);
  DL.push([266, () => ctx.drawImage(LAMPPOST_C, 0, 0)]);
  DL.push([BENCH.y + 5, () => ctx.drawImage(BENCH_C, 0, 0)]);
  DL.push([NB.y + 12, () => ctx.drawImage(NB_C, 0, 0)]);
  DL.push([NEAR_TABLE.base, () => {
    ctx.drawImage(NTABLE_C, 0, 0);
    const tx = NEAR_TABLE.x, ty = NEAR_TABLE.base - 18, fl = 0.75 + 0.25 * hash(5, step(t, 1440), 9);
    R(tx - 1, ty - 3, 3, 3, '#e8dcc8'); R(tx, ty - 2, 1, 1, fl > 0.85 ? '#fff6c8' : '#ffd890'); halo(HL.candle, tx, ty - 2, fl);
    if (crowd > 0) { R(tx - 5, ty - 2, 1, 2, '#b8243c'); R(tx + 4, ty - 2, 1, 2, '#b8243c'); }
  }]);
  DL.push([262.5, () => ctx.drawImage(PIPES_C, 0, 0)]);
  DL.push([262.2, () => drawPigeons(t)]);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
}

/* ================= Porto's own drizzle at three depths, rings on the river, splashes on the setts ================= */
// far drops small and slow, landing on the far bank and the river; mid ones on the river; near ones, longer and faster,
// passing in front of everything to the setts. Lamps light the drops near them.
const DRIZZLE = [
  { n: 150, nMin: 70, nMax: 92, len: 1, col: '#7c84b0', lit: '#c8a878', a: 0.34, land: [60, WL], slope: 0.12 },
  { n: 90, nMin: 100, nMax: 124, len: 2, col: '#969cc4', lit: '#e8c890', a: 0.4, land: [WL, KERB], slope: 0.16 },
  { n: 44, nMin: 150, nMax: 176, len: 4, col: '#b4b8dc', lit: '#ffe0b0', a: 0.48, land: [KERB, H + 6], slope: 0.22 },
].map((L, d) => ({ ...L, drops: Array.from({ length: L.n }, (_, i) => ({ n: L.nMin + Math.floor(hash(i, d, 71) * (L.nMax - L.nMin)), ph: hash(i, d, 72), s: i * 7 + d })) }));
const RAIN_LIGHTS = [{ x: LAMP.x, y: LAMP.y + 4, r: 44 }, { x: 446, y: 176, r: 30 }, { x: STRING.x0 + 20, y: STRING.y + 4, r: 24 }, { x: 92, y: 150, r: 30 }];
const RAIN_LIGHTS_BB = [...RAIN_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 4, r: 16 }];
const litAt = (x, y, lights) => lights.some(l => (x - l.x) ** 2 + (y - l.y) ** 2 < l.r * l.r);
function drawDrizzle(depth, t, lights) {
  const L = DRIZZLE[depth];
  ctx.globalAlpha = L.a;
  for (const d of L.drops) {
    const { cyc, u } = cycle(t, d.n, d.ph), land = lerp(L.land[0], L.land[1], hash(d.s, cyc, 73));
    const x0 = hash(d.s, cyc, 74) * (W + 40) - 20, y = Math.round(lerp(-8, land, u)), x = Math.round(x0 + (y - land) * L.slope);
    ctx.fillStyle = litAt(x, y, lights) ? L.lit : L.col;
    ctx.fillRect(x, y, 1, L.len);
  }
  ctx.globalAlpha = 1;
}
const RINGS = Array.from({ length: 34 }, (_, i) => ({ n: 150 + Math.floor(hash(i, 81) * 40), ph: hash(i, 82), s: i }));
function drawRings(t) {
  ctx.fillStyle = '#8aa6b4';
  for (const r of RINGS) {
    const { cyc, u } = cycle(t, r.n, r.ph);
    if (u > 0.3) continue;
    const x = Math.round(hash(r.s, cyc, 83) * W), y = Math.round(lerp(WL + 3, KERB - 2, hash(r.s, cyc, 84))), w = 1 + Math.round(u * 10);
    if (x > LODGE.x0 - 4 && y < KERB) continue;
    ctx.globalAlpha = 0.5 * (1 - u / 0.3);
    ctx.fillRect(x - w, y, 1, 1); ctx.fillRect(x + w, y, 1, 1);
    if (w > 2) { ctx.fillRect(x - w + 1, y - 1, 2 * w - 1, 1); ctx.fillRect(x - w + 1, y + 1, 2 * w - 1, 1); }
  }
  ctx.globalAlpha = 1;
}
const SPLASHES = Array.from({ length: 26 }, (_, i) => ({ n: 170 + Math.floor(hash(i, 85) * 40), ph: hash(i, 86), s: i }));
function drawSplashes(t) {
  ctx.fillStyle = '#b8bcdc';
  for (const r of SPLASHES) {
    const { cyc, u } = cycle(t, r.n, r.ph);
    if (u > 0.2) continue;
    const x = Math.round(hash(r.s, cyc, 87) * W), y = Math.round(lerp(KERB + 6, H - 2, hash(r.s, cyc, 88))), k = u < 0.1 ? 1 : 2;
    ctx.globalAlpha = 0.55 * (1 - u / 0.2);
    ctx.fillRect(x - k, y - k, 1, 1); ctx.fillRect(x + k, y - k, 1, 1); ctx.fillRect(x, y - 1 - k, 1, 1);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Porto draws its own drizzle; rain, a storm, snow and leaves come from the
// engine, landing on the far bank, the river and the setts and catching the same lamps
const WEATHER = weatherPlans(['drizzle']);
const WX_LAND = [[90, WL], [WL + 2, KERB], [KERB + 6, H + 4]];
const WX = { land: WX_LAND, lights: RAIN_LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })), leaves: { wind: [0, 0, W, 200] } };
const WX_BB = { ...WX, lights: RAIN_LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let MIST_C, HL, SKY_C, MOON_C, WISP_C, VALLEY_C, CLER_C, CITY_C, BLUFF_C, BRIDGE_C, WATER_C, RAB_C, QUAY_C, DECK_C, LODGE_C, BARREL_C, GULL_C,
  BB_C, BB_AD, PITCH, VIG_C, TRAM_C, TRAM_L, BOAT_C, CABIN_C, KIOSK_C, LAMPPOST_C, BENCH_C, NB_C, NTABLE_C, PIPES_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(14, '#fff2d0', 0.3, 1.5),
    haze: makeHalo(150, '#ff9a4a', 0.07, 1.3), cler: makeHalo(24, '#ffe8c0', 0.2, 1.6), clerTop: makeHalo(12, '#fff0d0', 0.22, 1.4),
    bell: makeHalo(30, '#fff0c8', 0.28, 1.5), se: makeHalo(20, '#ffd8a0', 0.12, 1.6), pilar: makeHalo(22, '#ffb860', 0.14, 1.6),
    arch: makeHalo(46, '#ffb84a', 0.16, 1.6), archCore: makeHalo(22, '#ffd070', 0.16, 1.5), deck: makeHalo(5, '#ffe2a0', 0.3, 1.4),
    low: makeHalo(6, '#ffd890', 0.35, 1.4), arcade: makeHalo(26, '#ffaa50', 0.16, 1.5), win: makeHalo(8, '#ffb45a', 0.18),
    lamp: makeHalo(56, '#ffb060', 0.22, 1.7), lampCore: makeHalo(14, '#ffe6b0', 0.5, 1.4),
    door: makeHalo(34, '#ffa850', 0.24, 1.5), lantern: makeHalo(18, '#ffc070', 0.4, 1.4), bulb: makeHalo(4, '#ffd890', 0.45, 1.2),
    candle: makeHalo(7, '#ffc070', 0.45, 1.3), sheen: makeHalo(60, '#d89a48', 0.07, 1.4), kiosk: makeHalo(30, '#ffb860', 0.2, 1.5),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(36, '#ffb870', 0.06, 1.5),
    tram: makeHalo(12, '#fff4d0', 0.4, 1.4), tramWin: makeHalo(26, '#ffe8b0', 0.14, 1.5), boat: makeHalo(28, '#ffc870', 0.18, 1.5),
    green: makeHalo(4, '#4aff8a', 0.5, 1.2),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp); MIST_C = renderMist();
  VALLEY_C = renderValley(); CLER_C = renderClerigos();
  layoutHouses(); CITY_C = renderCity(); BLUFF_C = renderBluff();
  BRIDGE_C = renderBridge(); WATER_C = renderWater(); RAB_C = RAB.map(renderRabelo);
  QUAY_C = renderQuay(); DECK_C = renderDeck(); LODGE_C = renderLodge(); BARREL_C = renderBarrel(); GULL_C = sprite(GULL, GULL_KEY);
  KIOSK_C = renderKiosk(); LAMPPOST_C = renderLampPost(); BENCH_C = renderBench(); NB_C = renderNearBollard(); NTABLE_C = renderNearTable();
  PIPES_C = renderPipes();
  TRAM_C = sprite(TRAM_ROWS, TRAM_KEY); TRAM_L = sprite(TRAM_ROWS, TRAM_KEY, true); BOAT_C = sprite(BOAT_ROWS, BOAT_KEY);
  CABIN_C = sprite(['..k..', 'kkkkk', 'kwwwk', 'kwwwk', 'kkkkk'], { k: '#2a2e3c', w: '#ffd890' });
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#1f4fa0' });
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
const ARCH_GLOWS = [[BR.xc - 80, 128, 0.8], [BR.xc - 40, 104, 0.7], [BR.xc, 98, 0.6], [BR.xc + 40, 104, 0.7], [BR.xc + 80, 128, 0.8]];
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
// someone crossing the lodge's lit upstairs room twice a loop
const UPSTAIRS = [[40, 50], [150, 160]];
function drawUpstairs(t) {
  const s = step(t, 960), tv = 0.45 + 0.55 * hash(13, s, 2);           // a television in the far room, its light changing
  ctx.globalAlpha = tv * 0.75; R(468, LODGE.eave + 7, 7, 15, ['#5a7ad8', '#8aa8f0', '#3a58b8', '#c8d8ff'][Math.floor(hash(12, s, 3) * 4)]); ctx.globalAlpha = 1;
  R(472, LODGE.eave + 7, 1, 15, '#3a2a30');
  const w = UPSTAIRS.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const gx = 425, gy = LODGE.eave + 7, u = (t - w[0]) / (w[1] - w[0]), cx = Math.round(gx - 3 + u * 13);
  ctx.save(); ctx.beginPath(); ctx.rect(gx, gy, 7, 11); ctx.clip();
  R(cx - 1, gy + 2, 3, 3, '#6a3a20'); R(cx - 2, gy + 5, 5, 8, '#6a3a20');
  ctx.restore();
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? RAIN_LIGHTS_BB : RAIN_LIGHTS;
  const m = metroAt(t), boat = boatAt(t), bell = bellGlow(t);
  // sky, stars, moon, wisps
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t);
  halo(HL.haze, 120, 170, 1);
  // the valley through the arch, Clérigos (glowing at the bell), the Sé, the Ribeira and its windows, Gaia's bluff
  ctx.drawImage(VALLEY_C, 0, 0);
  halo(HL.cler, CL.x, 70, 0.9); halo(HL.clerTop, CL.x, 40, 0.8);
  if (bell > 0) halo(HL.bell, CL.x, 78, bell);
  ctx.drawImage(CLER_C, 0, 0);
  if (bell > 0.3) { R(CL.x - 1, 78, 3, 3, '#3a2e2e'); R(CL.x + (Math.round(bell * 4) & 1 ? 1 : -1), 79, 1, 2, '#e8c070'); }
  halo(HL.se, 166, 72, 0.9);
  ctx.drawImage(CITY_C, 0, 0);
  drawWindows(t);
  drawTiny(t, 'quay', crowd);
  ctx.drawImage(BLUFF_C, 0, 0);
  halo(HL.pilar, 462, 62, 0.9);
  drawCableCar(t);
  for (let x = 8; x < 190; x += 22) halo(HL.arcade, x, 150, 0.85 + 0.15 * wave(t, 30, x * 0.01));
  if (wx.drizzle) drawDrizzle(0, t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // Dom Luís I: its glow, the iron, the deck lamps, the metro, the walkers on both decks; gulls through the arch
  for (const [x, y, a] of ARCH_GLOWS) halo(HL.arch, x, y + 10, a);
  ctx.drawImage(BRIDGE_C, 0, 0);
  for (const [x, y, a] of ARCH_GLOWS) halo(HL.archCore, x, y, a * 0.7);
  drawMetro(t, m);
  drawDeckLamps(t, m);
  drawTiny(t, 'top', crowd); drawTiny(t, 'low', crowd);
  for (let x = BR.l0 + 6; x <= BR.l1; x += 24) halo(HL.low, x, BR.ltop - 2, 0.9);
  drawFlock(t);
  // the Douro: base, the mirror of everything above, streaks under the lights, the cruise boat, the rabelos
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: KERB - WL, squash: 1.45, alpha: 0.28, amp: 0.5 });
  for (let x = 10; x < 190; x += 11) streak(ctx, t, x, 4, '#ffb860', 0.35, x * 0.7, WL + 1, KERB);
  for (let x = BR.xc - 90; x <= BR.xc + 90; x += 30) streak(ctx, t, x, 6, '#ffcc66', 0.3, x * 0.31, WL + 1, KERB);
  if (m) streak(ctx, t, m.x + (m.L >> 1), 14, '#fff0c0', 0.22, 9, WL + 2, KERB);
  ctx.globalAlpha = 0.55; drawScroll(MIST_C, WL - 12, 2, t); ctx.globalAlpha = 1;
  drawBoat(t, boat);
  RAB.forEach((r, i) => {
    const { c, ox, oy } = RAB_C[i], mid = r.x + (r.len >> 1), bob = Math.round(0.6 * wave(t, 40 + i * 8, i * 0.3) + 1.6 * wakeAt(t, mid) * wave(t, 160, i * 0.2));
    ctx.globalAlpha = 0.35; ctx.drawImage(c, 0, c.height - 9, c.width, 6, ox, oy + c.height + 1, c.width, 4); ctx.globalAlpha = 1;
    ctx.drawImage(c, ox, oy + bob);
  });
  if (wx.drizzle) { drawDrizzle(1, t, lights); drawRings(t); }
  // the quay, a gull on its bollard, the lamp's light on the wet stone
  ctx.drawImage(QUAY_C, 0, 0);
  drawBollardGull(t);
  halo(HL.sheen, BR.xc, KERB + 14, 0.8); halo(HL.sheen, 120, KERB + 12, 0.6);
  streak(ctx, t, LAMP.x, 6, '#ffc070', 0.34, 3, LAMP.y + 70, H);
  streak(ctx, t, (DOOR.x0 + DOOR.x1) / 2, 8, '#ffb060', 0.3, 7, TERRACE + 12, H);
  streak(ctx, t, (KIOSK.x0 + KIOSK.x1) >> 1, 10, '#ffb860', 0.22, 11, KIOSK.base + 2, H);
  for (const x of [BR.xc - 60, BR.xc, BR.xc + 60]) streak(ctx, t, x, 4, '#e8b050', 0.18, x * 0.13, KERB + 6, KERB + 40);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the lodge, its sign, its upstairs, the festoon, the terrace's tables
  ctx.drawImage(LODGE_C, 0, 0);
  ctx.drawImage(DECK_C, 0, 0);
  drawUpstairs(t);
  halo(HL.door, (DOOR.x0 + DOOR.x1) / 2, 186, 1);
  const lf = 0.85 + 0.15 * hash(3, step(t, 1440), 5);
  R(436, 164, 3, 5, '#1a1418'); R(437, 165, 1, 3, '#ffe2a0'); halo(HL.lantern, 437, 166, lf); halo(HL.win, 428, 142, 1);
  if (billboard) drawBillboard(t, pitch);
  const sway = wave(t, 30);
  for (let x = STRING.x0 + 2, i = 0; x < STRING.x1; x += 4, i++) {
    const u = (x - STRING.x0) / (STRING.x1 - STRING.x0), y = Math.round(STRING.y + Math.sin(Math.PI * u) * (5 + sway));
    R(x, y, 1, 1, '#2a2226'); R(x, y + 1, 1, 1, i % 3 ? '#ffd890' : '#ffeec0'); halo(HL.bulb, x, y + 1, 0.8);
  }
  TABLES.forEach((x, i) => {
    ctx.drawImage(BARREL_C, x - 5, TERRACE - 13);
    const fl = 0.75 + 0.25 * hash(i, step(t, 1440), 6);
    R(x - 1, TERRACE - 16, 2, 3, '#e8dcc8'); R(x, TERRACE - 17, 1, 1, fl > 0.85 ? '#fff6c8' : '#fff2c0'); halo(HL.candle, x, TERRACE - 16, fl);
    if (crowd > 0) R(x + 2, TERRACE - 16, 1, 2, '#b8243c');
  });
  R(377, TERRACE - 5, 7, 1, '#6a4a32'); R(378, TERRACE - 4, 1, 5, '#3a2a20'); R(382, TERRACE - 4, 1, 5, '#3a2a20');   // the stool
  // the wet setts under the kerb mirror the walkers and the lamps along it; then everything near, in feet order
  reflect(ctx, t, { top: KERB + 5, rows: 22, squash: 1, alpha: 0.2, amp: 0.4, k1: 30, k2: 70, x: 0, w: DECK.x0 - 2 });
  halo(HL.kiosk, (KIOSK.x0 + KIOSK.x1) >> 1, KIOSK.top + 26, 0.9);
  drawNear(t, crowd);
  halo(HL.lamp, LAMP.x, LAMP.y + 5, 1); halo(HL.lampCore, LAMP.x, LAMP.y + 5, 0.9);
  if (wx.drizzle) { drawDrizzle(2, t, lights); drawSplashes(t); }
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.65;
export default {
  id: 'porto',
  name: 'Porto',
  tagline: 'Gold arch over the Douro',
  timeZone: 'Europe/Lisbon',
  accent: '#1f4fa0',
  focusX: BR.xc,
  landmark: [CL.x - 7, BR.xc + BR.hsE],              // the Clérigos tower to the Dom Luís I arch's south foot
  landmarkTop: CL.tip,                               // the Clérigos cross; the bridge's deck lamps are at row 84
  billboard: BB,
  crowd: CROWD,
  spots: [                                           // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: BR.xc - BR.hsE, y: BR.top - 3, w: 2 * BR.hsE + 1, h: BR.yb - BR.top + 3 },   // the Dom Luís I bridge
    { kind: 'bell', x: CL.x - 6, y: CL.tip, w: 13, h: 62 },                                              // the Clérigos tower
    { kind: 'water', x: 160, y: WL + 2, w: 60, h: 18 },                                                  // the Douro, between the rabelos
  ],
  ambience: { rain: 0.3, water: 0.45, crowd: 0.18, wind: 0.12, traffic: 0.08 },
  events: [
    { t: 26, type: 'train', x: BR.d0 }, { t: 146, type: 'train', x: BR.d1 },
    { t: 60, type: 'bell', x: CL.x }, { t: 62.5, type: 'bell', x: CL.x }, { t: 65, type: 'bell', x: CL.x },
    { t: 61, type: 'gull', x: BOLLARDS[1] }, { t: 72, type: 'horn', x: 400 },
    { t: 180, type: 'bell', x: CL.x }, { t: 182.5, type: 'bell', x: CL.x }, { t: 185, type: 'bell', x: CL.x },
    { t: 181, type: 'gull', x: BOLLARDS[1] }, { t: 203, type: 'chime', x: 430, gain: 0.5 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
